// Typed API client. Mirrors the full surface of public/js/api.js so a port can
// drop in 1:1. Where the legacy code returns provider-specific shapes (e.g.
// Xtream proxy responses), we use `unknown` until a caller actually needs a
// narrower type — false precision would just bit-rot.
//
// All requests:
//   - send `credentials: 'same-origin'` so the auth_token cookie rides along
//   - forward `Bearer <localStorage.authToken>` if present (CLI/dev parity)
//   - on 401 redirect to /login (unless `allow401`)
//   - throw `ApiError` with status, body, and a friendly message on non-2xx
//
// Logging is via `log.*` — never `console.*`.

import { log } from './log';
import { redactUrlForLog } from './redact';

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly body: unknown,
        message: string,
        public readonly requestId: string | null = null,
    ) {
        super(message);
        this.name = 'ApiError';
    }
    /** Server-supplied machine-readable error code, when present. */
    get code(): string | null {
        if (this.body && typeof this.body === 'object') {
            if ('code' in this.body) {
                const code = (this.body as { code: unknown }).code;
                if (typeof code === 'string') return code;
            }
            if ('error' in this.body) {
                const legacyError = (this.body as { error: unknown }).error;
                if (typeof legacyError === 'string') return legacyError;
            }
        }
        return null;
    }
}

export interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    /** Suppress the 401 redirect; let the caller handle it. */
    allow401?: boolean;
    signal?: AbortSignal;
    headers?: Record<string, string>;
    /** Override base path; default is "/api". */
    base?: string;
}

/**
 * Strip credential-bearing query params from a URL before it reaches log.*.
 * `t` and `token` are stream-token JWTs; `url` may carry an upstream URL with
 * embedded `username`/`password` for some Xtream playlists. Phase 2.7 Section C
 * non-negotiable — tokens + probe URLs never appear in structured logs.
 */
function parseClientUrl(url: string): URL {
    const origin =
        typeof window !== 'undefined'
        && window.location?.origin
        && window.location.origin !== 'null'
            ? window.location.origin
            : 'http://nodecast.local';
    return new URL(url, origin);
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method ?? 'GET';
    const base = opts.base ?? '/api';
    const url = base + path;
    const logUrl = redactUrlForLog(url);

    const headers: Record<string, string> = { ...opts.headers };
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    try {
        const t = localStorage.getItem('authToken');
        if (t) headers['authorization'] = `Bearer ${t}`;
    } catch {
        // localStorage may be unavailable (Safari private mode); ignore.
    }

    const init: RequestInit = {
        method,
        credentials: 'same-origin',
        headers,
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
        ...(opts.signal ? { signal: opts.signal } : {}),
    };

    const start = performance.now();
    let res: Response;
    try {
        res = await fetch(url, init);
    } catch (err) {
        log.warn('api.network_error', { url: logUrl, method, error: (err as Error).message });
        throw err;
    }
    const elapsed = Math.round(performance.now() - start);
    const requestIdHeader = res.headers.get('X-Request-ID');
    const requestId = requestIdHeader && /^[A-Za-z0-9-]{8,64}$/.test(requestIdHeader)
        ? requestIdHeader
        : null;

    if (res.status === 401 && !opts.allow401) {
        log.info('api.unauthorized_redirect', { url: logUrl, method, status: res.status, ms: elapsed, requestId });
        try {
            localStorage.removeItem('authToken');
        } catch {
            // ignore
        }
        if (typeof location !== 'undefined' && !location.pathname.endsWith('/login')) {
            location.href = '/login';
        }
        throw new ApiError(401, null, 'Unauthorized', requestId);
    }

    let body: unknown = null;
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
        try {
            body = await res.json();
        } catch {
            body = null;
        }
    } else {
        try {
            body = await res.text();
        } catch {
            body = null;
        }
    }

    if (!res.ok) {
        const message =
            (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string'
                ? (body as { message: string }).message
                : null) ??
            (body && typeof body === 'object' && 'code' in body && typeof (body as { code: unknown }).code === 'string'
                ? (body as { code: string }).code
                : null) ??
            (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string'
                ? (body as { error: string }).error
                : null) ??
            `${method} ${logUrl} → HTTP ${res.status}`;
        log.warn('api.error', { url: logUrl, method, status: res.status, ms: elapsed, requestId });
        throw new ApiError(res.status, body, message, requestId);
    }

    log.debug('api.ok', { url: logUrl, method, status: res.status, ms: elapsed, requestId });
    return body as T;
}

// -------------------------- shapes (intentionally minimal)

export interface User {
    id: number;
    username: string;
    role: 'admin' | 'viewer';
}

export interface WatchTogetherMeasurement {
    kind: 'rewind-manifest' | 'seekable-range' | null;
    quality: 'known' | 'unavailable' | 'stale';
    secondsBehind: number | null;
}

export interface WatchTogetherParticipant {
    name: string;
    state: 'playing' | 'paused' | 'buffering';
    measurement: WatchTogetherMeasurement;
    canCatchUp: boolean;
    updatedAt: number;
}

export interface WatchTogetherMessage { id: string; name: string; text: string; at: number; }
export type WatchTogetherCommandStatus = 'pending' | 'checking' | 'applied' | 'already-near-live' | 'unsupported' | 'failed' | 'expired' | 'declined' | 'no-response';
export interface WatchTogetherCommandRecipient { id: string; name: string; status: WatchTogetherCommandStatus; sequence: number; }
export interface WatchTogetherCommandSummary { id: string; consentExpiresAt: number; recipients: WatchTogetherCommandRecipient[]; }
export interface WatchTogetherCommandResult { commandId: string; recipientId: string; name: string; status: WatchTogetherCommandStatus; sequence: number; at: number; }
export interface WatchTogetherSnapshot {
    revision: number;
    participants: WatchTogetherParticipant[];
    messages: WatchTogetherMessage[];
    /** Compatible sessions other than the playback session receiving this snapshot. */
    eligibleOtherSessionCount: number;
    /** Request summaries are visible only to the initiating playback session. */
    commands: WatchTogetherCommandSummary[];
}

/**
 * R1 (data-capture-riders §A1) — account snapshot persisted onto the source
 * record at sync time. Snapshot fields only; timestamps are unix ms.
 */
export interface AccountSnapshot {
    status: string;
    expDate: number | null;
    activeCons: number | null;
    maxConnections: number | null;
    isTrial: boolean;
    createdAt: number | null;
    snapshotAt: number;
}

export interface Source {
    id: number;
    name: string;
    type: 'xtream' | 'm3u' | 'epg' | 'stalker';
    url: string;
    enabled: boolean;
    /** R1 — present on Xtream sources that have synced since the rider landed. */
    accountSnapshot?: AccountSnapshot | null;
    /** Server returns more fields; expand as callers need them. */
    [k: string]: unknown;
}

/** Curated recommended-EPG-sources entry — surfaced via `GET /sources/recommended`. */
export interface RecommendedEpgSource {
    id: string;
    name: string;
    url: string;
    description: string;
    languages: readonly string[];
    estimatedChannels: number;
    license: string;
    homepage: string;
    /** Server annotation: true when a source with the same URL already exists. */
    alreadyAdded: boolean;
}

export interface RecommendedEpgSourcesResponse {
    schemaVersion: number;
    sources: RecommendedEpgSource[];
}

export interface Favorite {
    sourceId: number;
    itemId: string;
    itemType: 'channel' | 'movie' | 'series';
    addedAt?: string;
    /** Present for channel favourites resolved against the SQLite content index. */
    channel?: {
        name: string;
        streamIcon: string | null;
        groupTitle: string;
        inCurrentPlaylist: 0 | 1;
        /** Latest successful backend media delivery for this user/channel. */
        lastWatchedAt?: number | null;
    };
}

/**
 * Three distinct enums hide behind the legacy "item type" string. Splitting
 * them stops `bulkHide` from being callable with `'movie'` (no such backend
 * key) and stops `<ContentBrowser>`'s tab from being callable with
 * `'vod_category'` (a leaf key, not a tab).
 */

/** Favourites + per-user channel overrides. Backend: routes/favorites.js. */
export type ItemType = 'channel' | 'movie' | 'series';

/**
 * Hidden-set keys accepted by hide/show/bulkHide/bulkShow/getHidden — see
 * `mapItemType` in `server/routes/channels.js:9-17`. `group` cascades through
 * categories of type `live`; `vod_category`/`series_category` cascade through
 * `movie`/`series`. Per-item leaves (`movie` / `series`) are also accepted —
 * legacy `MoviesPage.js:140` and `SeriesPage.js:135` bulkHide call sites pass
 * those values; Phase 2.4 widens the union to match.
 */
export type HiddenItemType = 'channel' | 'group' | 'movie' | 'vod_category' | 'series' | 'series_category';

/** showAll / hideAll body + `<ContentBrowser>` tab. Backend: routes/channels.js:237. */
export type ContentType = 'channels' | 'movies' | 'series';

export interface HiddenItem {
    item_type: HiddenItemType;
    item_id: string;
    /** Server includes more fields (source_id, name); the consumer picks what it needs. */
    [k: string]: unknown;
}

/** Returned by `/sources/status` — one row per (source, sync-stripe). */
export interface SyncStatus {
    source_id: number;
    /** `'all'` is the umbrella row produced by `syncSource`; per-stripe rows ('live'|'vod'|'series'|'epg') also appear. */
    type: string;
    status: 'queued' | 'syncing' | 'success' | 'error';
    last_sync?: string | null;
    error?: string | null;
}

/**
 * Returned by `/sources/:id/account`. R1: the default read serves the
 * snapshot persisted at sync time (no upstream call, viewer-readable);
 * `refresh: true` (admin-only) performs one live player_api check.
 * `expDate` / `createdAt` / `snapshotAt` are unix ms — the server's
 * normalised shape (these were previously mistyped as ISO strings).
 */
export interface AccountStatus {
    status: 'active' | 'expired' | 'banned' | 'unknown' | 'n/a';
    expDate?: number | null;
    daysUntilExpiry?: number | null;
    activeStreams?: number | null;
    maxStreams?: number | null;
    createdAt?: number | null;
    isTrial?: boolean;
    message?: string | null;
    /** When the served data was captured; null when the source never synced since R1. */
    snapshotAt?: number | null;
}

/** Returned by `/sources/:id/estimate` and `/sources/estimate`. */
export interface M3uEstimate {
    count: number;
    needsWarning: boolean;
    /** Server may include more fields; agents pick what they read. */
    [k: string]: unknown;
}

export interface CustomGroup {
    id: number;
    name: string;
    [k: string]: unknown;
}

export interface GroupLayoutOverride {
    group_key: string;
    custom_name: string | null;
    user_hidden: number;
    updated_at: number;
}

/**
 * DVR recording row. Snake_case fields mirror the SQLite columns from
 * `server/db/sqlite.js:131-149`; the server returns rows verbatim via
 * `routes/recording.js` GET `/`.
 */
export type RecordingStatus = 'scheduled' | 'recording' | 'processing' | 'finished' | 'error' | 'missed' | 'cancelled' | 'broken';

export interface Recording {
    id: string;
    user_id: number;
    source_id: number | null;
    channel_id: string;
    channel_name: string | null;
    title: string | null;
    path: string | null;
    status: RecordingStatus;
    started_at: number;
    scheduled_start_ms: number | null;
    scheduled_duration_seconds: number | null;
    stream_id: string | null;
    stream_url: string | null;
    remote_url: string | null;
    error: string | null;
    size_bytes: number | null;
    stopped_at: number | null;
    /**
     * Spec 25 (post-4.7) — set on the parent row of a recurring schedule
     * (RRULE-lite string). NULL on one-shot recordings AND on child
     * occurrence rows.
     */
    recurrence_rule: string | null;
    /**
     * Spec 25 (post-4.7) — parent recording's PK on child occurrence rows.
     * NULL on one-shot recordings AND on the parent row itself.
     */
    parent_recording_id: string | null;
    /**
     * Spec C — user-renamed title; overrides `title` in the UI's headline.
     * NULL when the user has not customised it.
     */
    custom_title: string | null;
    /**
     * Spec C — exempts the row from any auto-cleanup
     * (`dvrRetention.shouldDeleteLocal` + `recordingJanitor.purgeBrokenOlderThan`).
     * Stored as 0/1 in SQLite; the typed shape mirrors that for fidelity
     * with `JSON.parse(SQL row)`.
     */
    keep_forever: 0 | 1;
    /**
     * X1 (ux-remediation D12) — ffprobe-measured duration in seconds,
     * written at finalize (`recordingRunner.js`). `null` when the row
     * hasn't been probed (older rows, or the probe itself failed/timed
     * out) — the client falls back to `stopped_at - started_at` in that
     * case (`RecordingCard`'s `durationStr`). This is the ONE authoritative
     * duration once present; never re-derive a second one from timestamps
     * when this is set.
     */
    probed_duration_seconds: number | null;
    /**
     * X1 (ux-remediation D2) — finalize-time verdict from the same probe:
     * `1` = the captured audio/video is directly playable in a browser,
     * `0` = it needs the transcode-audio-repair path (e.g. the out-of-spec
     * AAC the audit found — `[aac] Number of bands (46) exceeds limit
     * (45)`), `null` = unknown/not probed yet. Playback code MUST treat
     * `null` as "unknown, try direct" — only `0` short-circuits straight to
     * a transcode session.
     */
    browser_playable: 0 | 1 | null;
    /**
     * Buffer-export worker progress, projected by the list route while
     * `status === 'processing'`. Null for every persisted DVR state.
     */
    processing_progress?: number | null;
}

export interface BufferExportAudioTrack {
    id: string;
    label: string;
    language?: string | null;
    default?: boolean;
}

export interface BufferExportPreset {
    seconds: 1800 | 3600 | 7200;
    enabled: boolean;
    disabledReason?: string | null;
}

export interface BufferExportOptions {
    availableStartMs: number | null;
    availableEndMs: number | null;
    availableDurationSeconds: number;
    audioTracks: BufferExportAudioTrack[];
    busy: boolean;
    presets: BufferExportPreset[];
}

export type BufferExportRequest =
    | { playbackId: string; range: 'all' }
    | { playbackId: string; range: 'last'; seconds: 1800 | 3600 | 7200 };

export interface BufferExportResult {
    recordingId: string;
    plannedDurationSeconds: number;
    format: 'mkv';
    includedTracks: BufferExportAudioTrack[];
    omittedTracks: BufferExportAudioTrack[];
}

/**
 * Spec D — Recordings page filter + sort state. Persisted by
 * `useFiltersStore.recordings` (schemaVersion v6→v7) and consumed by
 * `<RecordingsFilters>` + the Pinia store's `*Filtered` getters.
 *
 * `statuses=[]` is the "no filter" sentinel — collapsing "all selected"
 * down to an empty array sidesteps the obvious footgun where a user
 * could end up with zero statuses checked and an empty list.
 */
export type RecordingSortKey =
    | 'date-desc'
    | 'date-asc'
    | 'size-desc'
    | 'channel-asc'
    | 'duration-desc';

export interface RecordingsFilterState {
    statuses: RecordingStatus[];
    /** Composite "<sourceId|->:<channelId>" matching the dropdown value. */
    channelKey: string | null;
    sourceId: number | null;
    search: string;
    sort: RecordingSortKey;
    /** Spec F — StoragePanel collapse state (panel hides body when true). */
    storagePanelCollapsed: boolean;
}

/**
 * Spec F — disk-headroom snapshot from `GET /api/recording/storage`.
 * `freeBytes` is null when the host doesn't expose `statfs` (the server
 * fail-opens; the panel renders "— GB free" without a colour cue).
 */
export interface StorageSnapshot {
    freeBytes: number | null;
    thresholdBytes: number;
    watermarkGb: number;
    ok: boolean;
}

/**
 * X0 — DVR data-safety. Response shape for `POST /recording/:id/stop`.
 * `stopped: true` is the normal "was actively recording, now finalized"
 * outcome; `alreadyFinished: true` is the honest no-op when the row had
 * already stopped (server does NOT delete anything in either case).
 */
export interface RecordingStopResult {
    ok: boolean;
    stopped?: boolean;
    /**
     * Seconds of media actually kept. Absent on some no-op replies, and
     * `null` (not just absent) when the row's `started_at` wasn't a finite
     * number server-side (`recording.js`'s `Number.isFinite(row.started_at)`
     * guard) — the client's formatter must treat both the same way.
     */
    keptSeconds?: number | null;
    alreadyFinished?: boolean;
    status?: RecordingStatus;
}

/**
 * X0 — DVR data-safety. Response shape for `DELETE /recording/:id`. One or
 * two discriminators are set per server outcome:
 *   - `cancelled` — the row was `scheduled`/`cancelled`; nothing was recording.
 *   - `deleted` — the row was `finished`/`error`/etc.; file(s) removed.
 *   - `deleted` + `stopped` (BOTH true together) — `?force=1` on an
 *     actively-`recording` row: the sink was stopped AND the (now-finalized)
 *     file was deleted.
 * The client must read these, never guess from the row's local `status`
 * (doctrine #1 — see docs/plans/ux-remediation.md X0).
 */
export interface RecordingDeleteResult {
    ok: boolean;
    stopped?: boolean;
    cancelled?: boolean;
    deleted?: boolean;
}

/** Body for PATCH /api/recording/:id — Spec C + Spec E. */
export interface RecordingPatchPayload {
    custom_title?: string | null;
    keep_forever?: boolean;
    /** Spec E — schedule shape. Time/channel/rule require status='scheduled'. */
    scheduled_start_ms?: number;
    scheduled_duration_seconds?: number;
    channel_id?: string;
    recurrence_rule?: string | null;
    /**
     * Spec E — required when the body touches schedule fields AND the row
     * participates in a recurrence (parent or child). 'this_occurrence'
     * detaches the row; 'all_future' (parent rows only) updates the parent
     * and drops every future scheduled child for the next scheduler tick
     * to top up under the new rule.
     */
    applyTo?: 'this_occurrence' | 'all_future';
}

/** Body for POST /recording/schedule. Server: routes/recording.js:121. */
export interface ScheduleRecordingPayload {
    sourceId: number;
    channelId: string;
    channelName?: string;
    streamId?: string;
    url?: string;
    title?: string;
    scheduledStartMs: number;
    durationSeconds: number;
    /**
     * Spec 32 — when true, server applies the configured `recordingPrePadSeconds` /
     * `recordingPostPadSeconds` on top of `durationSeconds` (clamped 0..600 / 0..1800
     * server-side). Phase 2.6 record-from-EPG sends `true`; the per-action +5 / +15
     * min picker adjusts `durationSeconds` separately. See `server/routes/recording.js:131-148`.
     */
    applyPads?: boolean;
    /**
     * Spec 25 (post-4.7) — RRULE-lite recurrence rule. When set, server
     * materialises N occurrences (1 parent + N-1 children) in a single
     * transaction; conflict detection runs against ALL occurrences.
     * Length capped at 200 chars server-side; supported tokens are
     * `FREQ=DAILY|WEEKLY|MONTHLY` (required), optional `BYDAY=MO,TU,...`,
     * `INTERVAL=N`, `UNTIL=YYYYMMDDTHHMMSSZ`.
     */
    recurrenceRule?: string;
    /**
     * R3 (data-capture-riders) — optional compact episode label ('S1E4')
     * from the EPG programme being recorded. Server appends it to the
     * on-disk filename (`<ts>-<name>-S1E4.mp4`); capped at 20 chars,
     * dropped for recurring schedules (each occurrence is a different
     * episode). Additive and optional at every layer.
     */
    episodeNum?: string;
}

/**
 * X4 (ux-remediation #9) — one existing scheduled/recording row whose
 * capture window overlaps the schedule that was just created. With a
 * 1-stream-per-source provider this is a guaranteed fire-time failure for
 * one of the two rows, regardless of channel — informational only, never a
 * block (unlike the ≥90% same-channel 409 `schedule_conflict`).
 */
export interface RecordingOverlapWarning {
    id: string;
    /** Server emits `custom_title ?? title ?? channel_name` — all three can be null. */
    title: string | null;
    channelName: string | null;
    scheduledStartMs: number;
    durationSeconds: number;
}

/**
 * X4 (ux-remediation #9) — `PATCH /recording/:id` carries the same soft
 * overlap warning as the schedule POST when the edit moved the capture
 * window into another row's. Omitted (not present) when there's nothing
 * to warn about.
 */
export type RecordingWithOverlapWarnings = Recording & {
    overlapWarnings?: RecordingOverlapWarning[];
};

/** Watch-history row. Server: routes/history.js GET '/' returns these with `data` JSON-parsed. */
export interface HistoryItem {
    id: string;
    user_id: number;
    source_id: number | null;
    item_type: 'live' | 'movie' | 'series' | 'episode';
    item_id: string;
    parent_id: string | null;
    progress: number;
    duration: number;
    updated_at: number;
    data: {
        title?: string;
        subtitle?: string;
        poster?: string;
        // Written by `useWatchHistoryTracker.buildPayload` and merged server-side
        // (routes/history.js). Required to resume provider VOD from the Continue
        // Watching rail — the item id alone can't resolve a stream URL.
        containerExtension?: string;
        seriesId?: string | number;
        currentSeason?: number;
        currentEpisode?: number;
        nextEpisode?: import('./player/seriesEpisodes').NextEpisodeMeta | null;
        continueWatchingDismissed?: boolean;
        [k: string]: unknown;
    };
    dwell_seconds: number | null;
    hidden: number | null;
    /** Atlas Phase 7 — explicit app-wide "watched" state (1 = user marked it done). */
    watched?: number | null;
}

/** One distinct live channel from backend-recorded delivery sessions. */
export interface ChannelViewHistoryItem {
    sessionId: string;
    sourceId: number;
    channelId: string;
    lastWatchedAt: number;
    activeSeconds: number;
    playbackMode: string;
    data: {
        title?: string;
        poster?: string | null;
        [k: string]: unknown;
    };
}

/**
 * Normalised VOD info — server source-of-truth: `server/services/vodInfoNormalise.js`
 * shape (also synthesised in `server/routes/proxy.js:522-538` for M3U sources).
 * Returned by `api.proxy.xtream.vodInfo()` inside the `info` field.
 */
export interface VodInfo {
    title: string;
    plot: string;
    cast: string;
    director: string;
    genre: string;
    genres: string[];
    durationSec: number | null;
    rating: number | null;
    year: string | number;
    posterUrl: string;
    backdropUrls: string[];
    trailerYoutubeId: string | null;
    tmdbId: string | null;
    containerExtension: string;
}

export interface VodMetadataGenre {
    key: string;
    name: string;
}

export interface VodMetadataPerson {
    key: string;
    name: string;
    /**
     * `creator` and `writer` were always returned by the server for series but
     * missing from this union, so every consumer that narrowed on it silently
     * dropped them.
     */
    role: 'cast' | 'director' | 'creator' | 'writer';
    billingOrder: number | null;
    /** Absent on a server older than the credit capture. */
    characterName?: string | null;
    job?: string | null;
    episodeCount?: number | null;
    department?: string | null;
    /** From the `vod_people` dictionary — 86.4% of series cast links have one. */
    profilePath?: string | null;
}

export interface VodEntitySeason {
    seasonNumber: number;
    name: string | null;
    airDate: string | null;
    episodeCount: number | null;
    posterPath: string | null;
}

export interface VodEntityNetwork {
    id: number;
    name: string | null;
    logoPath: string | null;
    originCountry: string | null;
}

export interface VodEntityEpisode {
    seasonNumber: number;
    episodeNumber: number;
    tmdbEpisodeId: number | null;
    name: string | null;
    overview: string | null;
    stillPath: string | null;
    airDate: string | null;
    voteAverage: number | null;
    voteCount: number | null;
    runtimeMinutes: number | null;
}

/**
 * Episode detail carries its own status because it is fetched on demand: an
 * empty list otherwise cannot be told apart from "not fetched yet".
 * `fetching` means a capture was kicked off — refetch shortly to pick it up.
 */
export interface VodEpisodeDetail {
    seasonNumber: number | null;
    status: 'ready' | 'fetching' | 'unavailable';
    episodes: VodEntityEpisode[];
    /**
     * Episode numbers the CALLING user has marked watched, for this season only.
     *
     * Ships with the season's episodes so a row renders its state on first paint
     * rather than flickering unwatched. Optional because the field is absent from an
     * older server and from surfaces that do not inject a reader.
     */
    watched?: number[];
}

/** Fresh watched state for one season — the reply to every watch-state write. */
export interface VodSeasonWatchState {
    seasonNumber: number;
    watched: number[];
    /** How many episodes the call actually changed. Absent on a plain read. */
    updated?: number;
}

export interface VodDetailMetadata {
    /**
     * The canonical title in the configured metadata language — what the Library
     * grid shows. Prefer it over the provider's `info.name`, which is decorated
     * for the provider's own catalogue ("AS - My Bias, My Boss (2026) (KR)").
     * Null when the entity has no resolved title, or on an older server.
     */
    title?: string | null;
    /** Entity-row facts. Absent on a server older than the enrichment change. */
    contentStatus?: string | null;
    originalTitle?: string | null;
    originalLanguage?: string | null;
    voteAverage?: number | null;
    voteCount?: number | null;
    genres: VodMetadataGenre[];
    people: VodMetadataPerson[];
    /** Every field below is absent on a server older than the enrichment change. */
    keywords?: { id: number; name: string }[];
    collection?: { id: number; name: string } | null;
    certifications?: { region: string; certification: string }[];
    trailer?: { site: string; videoKey: string; name: string | null; official: boolean } | null;
    seasons?: VodEntitySeason[];
    networks?: VodEntityNetwork[];
    /** Keyed by provider — `imdb`, `tvdb`. Consumers ask "do we have an IMDb id". */
    externalIds?: Record<string, string>;
    countries?: { code: string; name: string | null }[];
    languages?: { code: string; name: string | null }[];
    /**
     * TMDB title-logo art path ("/abc.png"), the Stremio-style hero treatment.
     * Compose onto the fixed image host after re-validating the path shape.
     * Absent on a server older than the logo capture.
     */
    logoPath?: string | null;
    recommendations?: {
        identityKey: string; kind: 'movie' | 'series';
        title: string | null; posterUrl: string | null; rank: number | null;
    }[];
    episodeDetail?: VodEpisodeDetail;
}

export interface VodGenreFacet extends VodMetadataGenre {
    entityCount: number;
}

export interface VodMetaFacets {
    genres: VodGenreFacet[];
    activePerson: Pick<VodMetadataPerson, 'key' | 'name'> | null;
    enrichment: {
        entities: number;
        entitiesEnriched: number;
        items: number;
        itemsCovered: number;
    };
    catalogEnrichment: {
        entities: number;
        entitiesEnriched: number;
        items: number;
        itemsCovered: number;
    };
}

export interface VodMetaFacetParams {
    kind: 'movie' | 'series';
    sourceId?: number | null;
    category?: string | null;
    genre?: string | null;
    person?: string | null;
    q?: string | null;
    searchPlot?: boolean;
}

/* ---------------------------------------------------------------------------
 * VOD Library (Phase 2) — entity-first contracts.
 *
 * Server source of truth: `server/services/vodLibrary.js` +
 * `server/routes/vodLibrary.js`. These describe logical titles, not playlist
 * items: one entry per entity regardless of how many provider variants back it.
 * ------------------------------------------------------------------------- */

export type VodLibraryKind = 'movie' | 'series';

export type VodLibrarySort =
    | 'added.desc'
    | 'release.desc'
    | 'title.asc'
    | 'rating.desc'
    | 'popularity.desc'
    /** Phase 3 — the caller's own last-watched time. Backs the Continue view. */
    | 'watched.desc';

export type VodLibraryState =
    | 'all'
    | 'favourites'
    | 'watched'
    | 'unwatched'
    | 'hidden'
    /** Phase 3 — started, not finished, not dismissed. Replaces History mode. */
    | 'continue';

/**
 * Typed error codes the Library API returns. `cursor_filter_mismatch` and
 * `cursor_sort_mismatch` arrive as HTTP 409 and mean "restart pagination";
 * everything else is a 400 and means "fix the request".
 */
export type VodLibraryErrorCode =
    | 'invalid_kind'
    | 'invalid_sort'
    | 'invalid_state'
    | 'invalid_limit'
    | 'invalid_search_query'
    | 'invalid_genre'
    | 'invalid_person'
    | 'invalid_keyword'
    | 'invalid_collection'
    | 'invalid_year'
    | 'invalid_year_range'
    | 'invalid_rating'
    | 'invalid_language'
    | 'invalid_country'
    | 'invalid_source'
    | 'category_requires_source'
    | 'invalid_identity_key'
    | 'invalid_cursor'
    | 'cursor_filter_mismatch'
    | 'cursor_sort_mismatch'
    | 'invalid_preferred_variant'
    | 'empty_patch'
    /** HTTP 503. Transient SQLite lock contention — the request is retryable. */
    | 'database_busy';

/** True when the failure is transient and the same request may simply be retried. */
export function isVodLibraryRetryable(err: unknown): boolean {
    return err instanceof ApiError && err.status === 503 && err.code === 'database_busy';
}

export const VOD_LIBRARY_CURSOR_ERRORS: readonly VodLibraryErrorCode[] = [
    'invalid_cursor',
    'cursor_filter_mismatch',
    'cursor_sort_mismatch',
] as const;

/** True when the error means the held cursor is unusable and paging must restart. */
export function isVodLibraryCursorError(err: unknown): boolean {
    if (!(err instanceof ApiError)) return false;
    const code = err.code;
    return code != null && (VOD_LIBRARY_CURSOR_ERRORS as readonly string[]).includes(code);
}

export interface VodLibraryEntitySummary {
    /** Stable address for detail and state calls, e.g. `movie:tmdb:603`. */
    identityKey: string;
    kind: VodLibraryKind;
    tmdbId: number | null;
    title: string;
    originalTitle: string | null;
    releaseDate: string | null;
    runtimeMinutes: number | null;
    voteAverage: number | null;
    voteCount: number | null;
    popularity: number | null;
    /** Bayesian-weighted score; null when the title has no vote evidence. */
    weightedRating: number | null;
    metadataStatus: 'pending' | 'enriched' | 'failed';
    firstAvailableAt: number | null;
    lastAvailableAt: number | null;
    variantCount: number;
    posterPath: string | null;
    /**
     * Ready-to-render artwork, resolved server-side: localized TMDB poster,
     * then the stored entity poster, then the best reachable provider icon.
     * Only 17.6 % of the catalog has TMDB art (Phase 3 §A.1) — never render
     * `posterPath` directly and expect a picture.
     */
    posterUrl: string | null;
    backdropPath: string | null;
    /**
     * The caller's own state for this title, or null if they have never touched
     * it. Present on the LIST so the grid can badge favourite / watched /
     * resume progress without one request per card.
     */
    state: VodLibraryListState | null;
}

/** List-shaped state. Omits `preferredPlaylistItemId`, which only detail needs. */
export interface VodLibraryListState {
    favorite: boolean;
    watched: boolean;
    libraryHidden: boolean;
    continueDismissed: boolean;
    progressSeconds: number | null;
    durationSeconds: number | null;
    lastWatchedAt: number | null;
}

export interface VodLibraryKeyword {
    id: number;
    name: string;
}

export interface VodLibraryCollection {
    id: number;
    name: string;
}

export interface VodLibraryCertification {
    region: string;
    certification: string;
}

/**
 * A trailer the server has already validated: the site is on its allowlist and
 * the key matches that site's id grammar. The client re-checks both before
 * composing an embed URL.
 */
export interface VodLibraryTrailer {
    site: 'YouTube';
    videoKey: string;
    name: string | null;
    official: boolean;
}

/**
 * vod-variant-picker Phase 2 — per-field-precedence quality reading. `null`
 * only when every field below is null (server merges measured
 * `vod_variant_facts` over a name/category-name parse, per-field, never
 * whole-object). `resHeight`/`hdr` may come from a probe/provider OR a text
 * parse; `bitrateKbps`/`durationSeconds` only ever come from measured facts.
 * Phase 8: `resHeight` is now always a tier (server-side `resolutionTier()`
 * folds raw measured dimensions, e.g. a cinemascope 3840×1600 → 2160).
 */
export interface VodLibraryVariantQuality {
    resHeight: 480 | 720 | 1080 | 1440 | 2160 | 4320 | null;
    hdr: 'hdr10' | 'dovi' | 'hlg' | null;
    bitrateKbps: number | null;
    durationSeconds: number | null;
    origin: 'provider' | 'probe' | 'name' | 'category' | null;
    /**
     * vod-variant-picker Phase 8 — ordered, deduped, lowercase ISO-639-1-ish
     * audio track languages measured from the file itself (probe, or series
     * provider aggregation). `null` when unknown. How much authority they
     * carry over `variant.language` depends on `audioLangsOrigin`.
     */
    audioLangs: string[] | null;
    /**
     * Provenance of `audioLangs` specifically — the facts row's origin
     * whenever langs are present, `null` otherwise. Distinct from `origin`,
     * which describes RESOLUTION-CLASS provenance and can say `'name'` on a
     * probe row whose dimensions weren't measured. `'probe'` langs are real
     * measurements (ffprobe read the streams) and overrule the name label;
     * `'provider'` langs are metadata claims from the same source as the
     * name and only fill in when the name carries no language.
     */
    audioLangsOrigin: 'provider' | 'probe' | null;
    /**
     * vod-variant-picker Phase 9 — measured codec facts (persisted since
     * Phase 2/7, served for the first time). Facts-only: the name-parse
     * never produces them; `null` when nothing was measured.
     */
    videoCodec: string | null;
    audioCodec: string | null;
    audioChannels: number | null;
    /**
     * vod-variant-picker Phase 9 — ordered, deduped, lowercase
     * ISO-639-1-ish subtitle track languages (probe only; provider payloads
     * carry no subtitle data). `und` dropped; `null` when unknown/empty.
     */
    subLangs: string[] | null;
    /**
     * R4 (data-capture-riders) — probe extras, facts-only. `fps` is the
     * YDIF-corrected frame rate; `bitDepth` is 8/10/12 from `pix_fmt` (the
     * "10-bit" pill gates on `>= 10`). `videoProfile`/`videoLevel`/`dvProfile`
     * feed the variant row's tech tooltip only — no pill of their own.
     */
    fps: number | null;
    bitDepth: number | null;
    videoProfile: string | null;
    videoLevel: number | null;
    dvProfile: number | null;
}

export interface VodLibraryVariant {
    sourceId: number;
    itemId: string;
    kind: VodLibraryKind;
    name: string;
    categoryId: string | null;
    /** Joined category name — also the fallback quality-signal source. */
    categoryName: string | null;
    containerExtension: string | null;
    language: string | null;
    isAdult: number;
    addedAt: string | null;
    quality: VodLibraryVariantQuality | null;
}

export interface VodLibraryEntityState {
    favorite: boolean;
    libraryHidden: boolean;
    continueDismissed: boolean;
    watched: boolean;
    progressSeconds: number | null;
    durationSeconds: number | null;
    lastWatchedAt: number | null;
    preferredPlaylistItemId: string | null;
}

export interface VodLibraryEntityDetail {
    identityKey: string;
    kind: VodLibraryKind;
    tmdbId: number | null;
    title: string;
    originalTitle: string | null;
    originalLanguage: string | null;
    plot: string | null;
    releaseDate: string | null;
    runtimeMinutes: number | null;
    voteAverage: number | null;
    voteCount: number | null;
    popularity: number | null;
    contentStatus: string | null;
    posterPath: string | null;
    /** Same resolution ladder as the list summary. */
    posterUrl: string | null;
    backdropPath: string | null;
    /**
     * TMDB title-logo art path ("/abc.png"), the Stremio-style hero treatment.
     * Compose onto the fixed image host after re-validating the path shape.
     * Absent on a server older than the logo capture.
     */
    logoPath?: string | null;
    metadataStatus: 'pending' | 'enriched' | 'failed';
    genres: VodMetadataGenre[];
    people: VodMetadataPerson[];
    keywords: VodLibraryKeyword[];
    collection: VodLibraryCollection | null;
    certifications: VodLibraryCertification[];
    trailer: VodLibraryTrailer | null;
    variants: VodLibraryVariant[];
    state: VodLibraryEntityState | null;
}

export interface VodLibraryPage {
    items: VodLibraryEntitySummary[];
    /** Opaque, server-signed. Null means this is the last page. */
    nextCursor: string | null;
    sort: VodLibrarySort;
    limit: number;
    total: number;
}

export interface VodLibraryCoverage {
    entities: number;
    enriched: number;
}

export interface VodLibraryFacets {
    genres: Array<{ key: string; name: string; count: number }>;
    /** Denominators for the filtered slice. */
    coverage: VodLibraryCoverage;
    /** Denominators for the same scope minus discovery filters. */
    catalogCoverage: VodLibraryCoverage;
}

export interface VodLibraryQuery {
    kind: VodLibraryKind;
    q?: string | null;
    genre?: string | null;
    person?: string | null;
    keyword?: number | string | null;
    collection?: number | string | null;
    yearFrom?: number | null;
    yearTo?: number | null;
    ratingMin?: number | null;
    language?: string | null;
    country?: string | null;
    sourceId?: number | null;
    category?: string | null;
    state?: VodLibraryState | null;
    sort?: VodLibrarySort | null;
    searchPlot?: boolean;
    cursor?: string | null;
    limit?: number | null;
}

export interface VodLibraryStatePatch {
    favorite?: boolean;
    libraryHidden?: boolean;
    continueDismissed?: boolean;
    watched?: boolean;
    progressSeconds?: number;
    durationSeconds?: number;
    lastWatchedAt?: number;
    preferredPlaylistItemId?: string | null;
}

/**
 * Newest-request-wins cancellation.
 *
 * Library browsing fires a request per keystroke and per facet toggle. Without
 * this, a slow early response can land after a fast later one and repaint the
 * grid with stale rows. Each lane keeps at most one in-flight request; starting
 * a new one aborts its predecessor.
 */
const vodLibraryLanes = new Map<string, AbortController>();

export function abortVodLibraryLane(lane: string): void {
    vodLibraryLanes.get(lane)?.abort();
    vodLibraryLanes.delete(lane);
}

function latestOnly(lane: string | undefined): AbortSignal | undefined {
    if (!lane) return undefined;
    vodLibraryLanes.get(lane)?.abort();
    const controller = new AbortController();
    vodLibraryLanes.set(lane, controller);
    return controller.signal;
}

function vodLibraryQs(query: VodLibraryQuery): string {
    return qs({
        kind: query.kind,
        q: query.q,
        genre: query.genre,
        person: query.person,
        keyword: query.keyword,
        collection: query.collection,
        yearFrom: query.yearFrom,
        yearTo: query.yearTo,
        ratingMin: query.ratingMin,
        language: query.language,
        country: query.country,
        sourceId: query.sourceId,
        category: query.category,
        state: query.state,
        sort: query.sort,
        searchPlot: query.searchPlot ? 1 : undefined,
        cursor: query.cursor,
        limit: query.limit,
    });
}

export type VodEnrichmentLane = 'tmdb' | 'provider';
export type VodEnrichmentJobStatus = 'pending' | 'in_flight' | 'done' | 'failed_retryable' | 'failed_permanent';

export interface VodEnrichmentLaneStatus {
    paused: boolean;
    cooldownUntil: number | null;
    stoppedReason: string | null;
    queue: Record<VodEnrichmentJobStatus, number>;
    lastAnomaly: { errorClass: string; httpStatus: number | null; at: number } | null;
    oldestPendingAgeMs: number | null;
    currentRatePerSecond: number;
}

export interface VodMetadataReconciliationStatus {
    kind: 'movie' | 'series';
    completedThrough: string | null;
    mode: 'idle' | 'changes' | 'full';
    windowStart: string | null;
    windowEnd: string | null;
    nextPage: number;
    entityCursor: number;
    metadataRegion: string | null;
    metadataLanguage: string | null;
    normalizerVersion: number | null;
    returnedCount: number;
    matchedCount: number;
    queuedCount: number;
    updatedAt: number;
}

/** All the server will ever tell a client about a stored credential. */
export interface TmdbCredentialPresence {
    tmdbReadTokenSet: boolean;
    tmdbApiKeySet: boolean;
}

/**
 * D7 (ux-remediation X6) — Spec 15 adult-content PIN set/change. The PIN
 * itself is write-only: the server hashes it (`services/pinHash.js`) and
 * only ever reports presence (`adultPinSet` on `GET /api/settings`), never
 * the hash or a masked form of the value.
 */
export interface AdultPinSetResult {
    ok: boolean;
}

/**
 * Verdict from probing TMDB. `ok: false` with a reason is a normal answer —
 * "your token is wrong" is information, not a transport failure.
 */
export interface TmdbCredentialTest {
    ok: boolean;
    reason?: 'credentials_missing' | 'auth_invalid' | 'rate_limited' | 'upstream_error';
    status?: number;
    using?: 'readToken' | 'apiKey';
}

export interface VodEnrichmentStatus {
    enabled: boolean;
    tmdbConfigured: boolean;
    /**
     * Which layer supplied the credential in effect. Surfaced so a stale
     * environment variable can never masquerade as the token an admin just
     * saved. Absent on servers older than the credential-management change.
     */
    tmdbCredentialSource?: 'settings' | 'env' | null;
    /**
     * Why a started lane is nonetheless standing still: the worker yields to a
     * running source sync and to active playback. Absent on servers older than
     * this field — treat as "no deferral known" rather than "not deferred".
     */
    deferredReason?: 'sync' | 'playback' | null;
    globalPaused: boolean;
    lanes: Record<VodEnrichmentLane, VodEnrichmentLaneStatus>;
    enrichment: {
        entities: number;
        entitiesEnriched: number;
        items: number;
        itemsCovered: number;
    };
    expiringCount: number;
    reconciliation: VodMetadataReconciliationStatus[];
}

/**
 * Phase 1B — independently configurable TMDB capture region and response
 * language. These values affect normalization and full reconciliation, never
 * logical-title identity.
 */
export interface MetadataSettings extends Record<string, unknown> {
    metadataRegion: string;
    metadataLanguage: string;
}

export interface SettingsSnapshot extends Record<string, unknown> {
    metadataRegion?: string;
    metadataLanguage?: string;
    footballFixturesEnabled?: boolean;
    footballBroadcastMarkets?: string[];
    footballFixtureLocale?: 'FR' | 'GB' | 'US' | 'DE' | 'AT' | 'CH' | 'RU';
}

/**
 * X5 (ux-remediation U4/D8-final) — per-user player preferences. These eight
 * keys used to live in the global `/api/settings` bag (admin-only, one value
 * for the whole household); they're per-user now. Server:
 * `server/routes/me.js`, backed by `server/db.js`'s `getDefaultPreferences()`
 * (the single source of the eight defaults — do not hand-copy them here).
 * `GET /api/me/preferences` always resolves every key (stored overrides
 * merged over defaults); `PUT` accepts a partial patch and returns the same
 * full resolved shape. The shape is CLOSED — no `[k: string]: unknown`
 * escape hatch — every consumer reads a real key, never a guessed one.
 */
export interface UserPreferences {
    arrowKeysChangeChannel: boolean;
    /** Seconds the channel info overlay stays visible. Range 1..30. */
    overlayDuration: number;
    /**
     * 0..100. With `rememberVolume` off this is the level EVERY tune-in
     * starts at (Vidstack re-asserts the bound volume attribute on each
     * media-ready, so a mid-session slider change lasts only until the next
     * channel hop) — a pinned default, not a one-time initial value.
     */
    defaultVolume: number;
    rememberVolume: boolean;
    /**
     * 0..100. PLAYER STATE, not a Settings-page control (no dedicated UI) —
     * written by `<NodecastPlayer>`'s debounced volumechange write-back when
     * `rememberVolume` is true, read back as the initial volume on next mount.
     */
    lastVolume: number;
    autoPlayNextEpisode: boolean;
    /** ISO-ish language code ('de', 'en', 'pt-BR') or null (off / provider default). */
    captionsDefaultLang: string | null;
    audioDefaultLang: string | null;
    /**
     * Subtitle appearance, rendered as `::cue` rules by the player.
     * Per-user rather than a server setting: legibility is personal and an
     * accessibility need, so a viewer changes it without admin rights.
     * Legal values live in PREFERENCE_ENUMS (server/routes/me.js).
     */
    subtitleFontSize: 'small' | 'medium' | 'large' | 'xlarge';
    subtitleBackground: 'box' | 'outline' | 'none';
    subtitleTextColour: 'white' | 'yellow';
    footballFavoriteLeagueIds?: string[];
}

export interface VodCatalogFilterOptions {
    includeHidden?: boolean;
    genre?: string;
    person?: string;
    q?: string;
    searchPlot?: boolean;
}

/**
 * Spec 12 VOD variant (alternate language/source/edition cluster). Server source-
 * of-truth: `server/services/vodVariantCluster.js:79-83` (`tagType`) and
 * `server/routes/proxy.js:560-595`. Returned by `api.proxy.xtream.vodInfo()`
 * inside the `variants` array.
 */
export interface Variant {
    sourceId: number;
    streamId: string;
    name: string;
    tag: string | null;
    /** Sort order: language(0) → source(1) → undefined/edition(2). */
    type: 'language' | 'source' | undefined;
    language: string | null;
}

/**
 * Episode row inside `seriesInfo.episodes[seasonNum]`. Xtream's `getSeriesInfo`
 * passthrough shape (server: `proxy.js:446-475`); M3U sources synth equivalent
 * fields via `buildM3uSeriesInfo`. Field set is the legacy-consumed surface
 * (`SeriesPage.js:644-672`).
 */
export interface Episode {
    id: string;
    episode_num: number | string;
    title?: string;
    container_extension?: string;
    info?: {
        movie_image?: string;
        name?: string;
        duration_secs?: number | string;
        duration?: number | string;
        plot?: string;
        description?: string;
        [k: string]: unknown;
    };
}

/** Series-info envelope — raw Xtream `{info, episodes}` shape. */
export interface SeriesInfo {
    info?: {
        backdrop_path?: string[];
        cover?: string;
        releaseDate?: string;
        releasedate?: string;
        rating?: string | number;
        genre?: string;
        plot?: string;
        cast?: string;
        director?: string;
        youtube_trailer?: string;
        [k: string]: unknown;
    };
    episodes: Record<string, Episode[]>;
    metadata?: VodDetailMetadata | null;
}

/** POST body for `/api/transcode/session`. Server: routes/transcode.js:84-159. */
export interface TranscodeSessionPayload {
    /** Tokenised loopback URL (preferred) or raw upstream URL. One of token / url required. */
    url?: string;
    token?: string;
    /** Initial seek offset in seconds. Used by VOD scrubbing. */
    seekOffset?: number;
    /** Playback allows only `'copy'`; `'encode'` is retained for server-side rejection tests. */
    videoMode?: 'copy' | 'encode';
    videoCodec?: string;
    audioCodec?: string;
    audioChannels?: number;
    /**
     * Source audio track to map (`-map 0:a:N`, relative index from
     * `ProbeInfo.audioStreams[].index`). Absent/0 = first stream (the
     * pre-feature behaviour). The server validates it (400 shape / 422
     * out-of-range) and resolves the SELECTED track's codec/channels for
     * its smart-copy decision, so `audioCodec`/`audioChannels` here may
     * describe track 0 without harm.
     */
    audioStreamIndex?: number;
    /** Total source duration in seconds — when known, drives the synthetic VOD playlist. */
    duration?: number;
}

/**
 * X1 (ux-remediation D2) — recording-scoped alternative POST body for
 * `/api/transcode/session`. The server resolves the local file itself via a
 * user-scoped `resolveRecordingFile()` lookup, keyed on `recordingId` — the
 * client never constructs or sends a local file URL, so `assertPublicUrl`
 * (unchanged, doctrine #3) never gets a chance to reject it (the 400 the
 * audit saw when the old code tried to transcode-fallback a recording's
 * file path). Server error discriminators: 404 unknown/foreign row, 409
 * `{error:'not_finished'}` (row isn't finished/error yet), 422
 * `{error:'file_missing'}` (row is finished but the file is gone).
 */
export interface TranscodeSessionRecordingPayload {
    recordingId: string;
    /** Initial seek offset in seconds. Used by VOD scrubbing. */
    seekOffset?: number;
}

export interface TranscodeSessionResult {
    sessionId: string;
    /** Path to the synthetic HLS playlist; pass to `<video src>` after token-stripping. */
    playlistUrl: string;
    ready: boolean;
}

export type LivePlaybackMode = 'catchup' | 'rewind' | 'transcode' | 'remux' | 'proxy';
export type LivePlaybackForceMode = 'transcode';

export interface LivePlaybackSessionRequest {
    sourceId: number;
    channelId: string;
    streamFormat?: string;
    /** Dumb retry override — the backend picks codecs; no codec fields are sent. */
    forceMode?: LivePlaybackForceMode;
    catchup?: { startMs: number; durationMin: number };
    playTraceId?: string | null;
    /** Opaque per-tune correlation ID; playTraceId remains accepted for older clients. */
    playbackAttemptId?: string | null;
    /**
     * Stable identity for this browser tab, retained across reloads. The
     * backend uses it only to auto-replace this same user's browser playback;
     * another tab/device and every external-player session still require
     * explicit confirmation.
     */
    clientSessionId?: string;
    /**
     * Admin-only: steal another user's slot on a capped source (the busy
     * 409 carried `canTakeover: true`). Ignored server-side for viewers.
     */
    takeover?: boolean;
    /**
     * live-buffer-resilience — explicit same-user slot-conflict consent.
     * Names the playbackId(s) (up to 8) this tune is entitled to replace —
     * the requester's OWN other session(s) on the source, either the one
     * being superseded by this very tune or the set surfaced by a prior
     * `409 source_slot_busy { requiresReplace: true, ownSessions }`. The
     * backend steals ONLY sessions owned by the requester; a foreign
     * occupant still 409s regardless of this field.
     */
    replacePlaybackId?: string | string[];
    /**
     * X2 (ux-remediation D3) — explicit user Retry bypasses the server's
     * probe/preflight cache (short TTL, but a slot-busy/offline retry inside
     * that window would otherwise fail from cache after the real cause
     * cleared). Sent ONLY on user-initiated retries — first tune-in omits
     * this field entirely so first-attempt behavior is completely unchanged.
     */
    fresh?: boolean;
}

export interface LivePlaybackSessionResult {
    playbackId: string;
    mode: LivePlaybackMode;
    url: string;
    probeInfo: ProbeInfo | null;
    cleanupUrl: string;
    sinkId?: string;
    sessionId?: string;
    sessionStartedAtMs?: number;
    elapsedSeconds?: number;
    readyReason?: string | null;
    reused?: boolean;
    segmentSec?: number;
    initialSegments?: number | null;
    availableDurationSeconds?: number | null;
    shared?: boolean;
    retentionSeconds?: number;
    activeViewers?: number;
    bufferStartedAtMs?: number;
    /** Rewind only: whether the shared buffer's sink is downmixing audio. */
    audioRepair?: 'copy' | 'transcode' | null;
    /**
     * live-audio-track-selection Phase 4 — the CHANNEL's upstream track
     * counts. Rewind only: that path's `probeInfo` describes the LOCAL
     * buffer output, which is single-audio by construction, so it can never
     * answer "how many tracks does this channel carry". Other live modes
     * probe upstream directly and already carry the true counts in
     * `probeInfo` — consumers prefer this field and fall back.
     */
    upstreamTracks?: {
        audio: number | null;
        subtitle: number | null;
        /**
         * live-subtitle-tracks — how many of `subtitle` a BROWSER can show.
         * Separate from the raw count on purpose: on DVB live channels the
         * honest pair is `{ subtitle: 3, subtitleRenderable: 0 }`. `null` is
         * "not measured", `0` is "measured, none renderable".
         */
        subtitleRenderable?: number | null;
        /**
         * What the tracks are — `'bitmap'` (unreachable for any browser) or
         * `'teletext'` (unreachable for this server: no libzvbi). `null`
         * when the tracks disagree or the format is unrecognised, in which
         * case the UI must stay format-agnostic rather than guess.
         */
        subtitleFormat?: SubtitleFormat;
        /**
         * True when the shared sink is emitting the external-player feed that
         * carries these tracks (`/api/live-ts?subs=1`). Derived from the real
         * emission plan, so the UI only points at the external player when
         * the subtitles are genuinely there.
         */
        subtitleExternal?: boolean;
    } | null;
    /**
     * live-buffer-resilience — epoch-based sink-restart continuity state.
     * Populated for `mode: 'rewind'` (live-buffer-backed) sessions; `null`
     * for transcode/proxy/remux/catchup. `'reconnecting'` means the shared
     * buffer's sink died and a respawn is pending — the composed manifest
     * still serves a frozen tail (no 404s), so playback of already-buffered
     * media can continue uninterrupted while this is true.
     */
    bufferState?: 'live' | 'reconnecting' | null;
    degradedReason?: string | null;
    /**
     * live-buffer-resilience — severity, ORTHOGONAL to `degradedReason`:
     * `true` once the sink has flapped 3+ times inside the stability
     * window. Separate from the cause on purpose — an escalation used to
     * overwrite `degradedReason` with `'channel_unstable'`, which threw
     * away the more actionable fact (whose fault it is).
     */
    degradedUnstable?: boolean;
    lastSegmentAtMs?: number | null;
    bufferDepthSeconds?: number | null;
}

/**
 * live-buffer-resilience — one of the REQUESTER'S OWN other sessions on a
 * capped source, surfaced by a `409 source_slot_busy { requiresReplace: true }`
 * response so the client can offer an explicit one-click "stop it & watch
 * here" takeover. Naming every entry's `playbackId` in a retry's
 * `replacePlaybackId` field makes the retry succeed with no consent prompt
 * (it's the requester's own playback, not a foreign steal).
 */
export interface OwnOccupyingSession {
    playbackId: string;
    sourceId: number;
    channelId: string;
    mode: LivePlaybackMode | 'live-hls' | 'live-ts' | 'proxy-hls';
    client: 'browser' | 'external-player';
    startedAt: number;
}

/** Body for POST /api/playback/external-session. */
export interface ExternalPlaybackSessionRequest {
    sourceId: number;
    channelId: string;
    /** Correlates the handoff across NodeCast and external-player requests. */
    playbackAttemptId?: string | null;
    transport?: 'hls' | 'ts';
    /**
     * live-subtitle-tracks — "I want the channel's subtitle tracks". A
     * DIFFERENT question from `transport`: the tracks exist only in the raw
     * TS feed, so this implies it, but a buffer with no subtitle feed
     * degrades to the HLS default rather than handing back raw TS with no
     * subtitles. Check `mode` in the result to see which you got.
     */
    subtitles?: boolean;
    /** See `LivePlaybackSessionRequest.replacePlaybackId` — identical consent policy. */
    replacePlaybackId?: string | string[];
}

/** Result of POST /api/playback/external-session (live-ingest-tee + remediation). */
export interface ExternalPlaybackSessionResult {
    /** `live-hls` (default) = full-fidelity tee served as real HLS; `live-ts` = continuous raw TS (non-default `transport: 'ts'` opt-in — the Live "External + subtitles" action is the one UI caller); `proxy-hls` = rewind-disabled fallback. */
    mode: 'live-hls' | 'live-ts' | 'proxy-hls';
    url: string;
    /** Present for every backend-attributed external playback mode. */
    playbackId?: string;
    cleanupUrl?: string;
    /** live-buffer-resilience — degraded-state fields; present for live-hls/live-ts, absent/null for proxy-hls. */
    bufferState?: 'live' | 'reconnecting' | null;
    degradedReason?: string | null;
    /** See `degradedUnstable` above — severity, orthogonal to the cause. */
    degradedUnstable?: boolean;
    lastSegmentAtMs?: number | null;
    bufferDepthSeconds?: number | null;
}

export interface LiveBufferRule {
    id: number;
    sourceId: number;
    channelId: string;
    enabled: boolean;
    priority: number;
    daysMask: number;
    startMinute: number;
    endMinute: number;
    createdAt: number;
    updatedAt: number;
}

export interface LiveBufferRuleInput {
    sourceId: number;
    channelId: string;
    enabled: boolean;
    priority: number;
    daysMask: number;
    startMinute: number;
    endMinute: number;
}

/**
 * One warm-keep target's viewer-safe status (live-ingest-tee-remediation) —
 * present for BOTH viewer and admin payloads, unlike `rules`/`events`. No
 * sinkId/playlistUrl/ruleId. `reason` + `nextAttemptAtMs` are set only for
 * `state === 'backoff'`.
 */
/**
 * One user watching a channel right now. Admin-only — the server attaches this
 * to warm targets + buffers only when the requester is an admin (a viewer's
 * status payload never carries names). `sessions` > 1 means the same user is
 * watching from more than one place (e.g. browser tab + external player).
 */
export interface LiveViewer {
    userId: number;
    username: string;
    sessions: number;
}

export interface WarmTarget {
    sourceId: number;
    channelId: string;
    channelName: string | null;
    state: 'active' | 'starting' | 'waiting_slot' | 'backoff' | 'scheduled_off';
    activeViewers: number;
    reason?: 'channel_offline' | 'stream_limit' | 'transient';
    nextAttemptAtMs?: number;
    /** Admin-only — who is watching (undefined for viewers, [] when nobody). */
    viewers?: LiveViewer[];
}

export interface LiveBufferStatus {
    buffers: Array<{
        sourceId: number;
        channelId: string;
        bufferStartedAtMs: number;
        retentionSeconds: number;
        activeViewers: number;
        channelName: string | null;
        sinkId?: string;
        warmRuleIds?: number[];
        playlistUrl?: string;
        /** Non-admin snapshot only — mirrors `warmRuleIds.length > 0`. */
        warm?: boolean;
        /**
         * live-buffer-resilience — epoch-based sink-restart continuity.
         * `'reconnecting'` means the sink died and a respawn is pending; the
         * buffer still serves a frozen composed manifest (not a 404) while
         * this is true — an early-warning signal, not necessarily a
         * playback failure yet. Already live server-side; typed here for
         * `useBufferHealth`'s one-shot + poll check.
         */
        bufferState?: 'live' | 'reconnecting';
        degradedReason?: string | null;
        /** See `degradedUnstable` above — severity, orthogonal to the cause. */
        degradedUnstable?: boolean;
        lastSegmentAtMs?: number | null;
        bufferDepthSeconds?: number;
        generation?: number;
        epochCount?: number;
        /** Admin-only — who is watching (undefined for viewers, [] when nobody). */
        viewers?: LiveViewer[];
    }>;
    /** Present for every user (admin + viewer) — the pill/badge feed. */
    warmTargets?: WarmTarget[];
    rules?: LiveBufferRule[];
    events?: Array<{
        id: number;
        at: number;
        event: string;
        sourceId: number | null;
        channelId: string | null;
        sinkId: string | null;
        detail: unknown;
    }>;
    /** Admin-only — raw per-channel warm-failure/backoff state. */
    warmBackoff?: Array<{
        sourceId: number;
        channelId: string;
        kind: 'slot_busy' | 'channel_offline' | 'stream_limit' | 'transient';
        consecutiveFailures: number;
        lastStatus: number | null;
        skipUntilMs: number;
        firstFailureAtMs: number;
        lastFailureAtMs: number;
    }>;
}

export interface ActivePlaybackSession {
    playbackId: string;
    userId: number | string;
    username: string;
    kind: 'live' | 'vod';
    mode: string;
    contentType: 'movie' | 'episode' | 'recording' | null;
    contentId: string | null;
    title: string | null;
    sourceId: number | null;
    playbackState: 'playing' | 'paused' | 'buffering';
    currentTime: number | null;
    startedAt: number;
    lastSeenAt: number;
}

/**
 * EPG channel row from `GET /api/proxy/epg/:sourceId`. The server's hardcoded
 * ±24h window joins `playlist_items` (type='epg_channel') with `epg_programs`
 * — see `server/routes/proxy.js:1162-1256`. `id` is the XMLTV `tvg-id`; `name`
 * comes from `display_name`; `icon` is the channel icon (may be null); `language`
 * is ISO-639-1 (may be null).
 */
export interface EpgChannel {
    id: string;
    name: string;
    icon: string | null;
    language: string | null;
    /**
     * Populated only when the request includes `channelsOnly=1` (picker mode —
     * see `server/services/epgQuery.js`). Counts `epg_programs` rows for this
     * `(sourceId, channelId)` within the server's ±24h window; the picker
     * uses this to grey out / hide channels that would override a live row
     * with a target that has no programmes.
     */
    programmeCount?: number;
}

/**
 * EPG programme row. `start` and `stop` are ISO-8601 strings (`server/routes/proxy.js:1241-1248`).
 * Consumers parse to `Date.getTime()` for time-axis math; the store stores both raw
 * strings and pre-computed ms timestamps (see `web/src/stores/epg.ts`).
 */
export interface Programme {
    channelId: string;
    start: string;
    stop: string;
    title: string;
    description?: string;
    /**
     * R3 (data-capture-riders) — compact episode label ('S1E4'), normalized
     * server-side at XMLTV parse time from `<episode-num>` (xmltv_ns 0-based
     * and onscreen shapes both fold into this form). Absent when the feed
     * carries none — the server omits null keys to keep the window payload
     * lean.
     */
    episodeNum?: string;
    /**
     * R3 — programme-level XMLTV `<icon src>` artwork URL. Absent when the
     * feed carries none. Route through `proxyImageUrl` before use in
     * `<img src>` (same rule as channel logos).
     */
    icon?: string;
}

export interface EpgWindowChannel {
    liveId: string;
    sourceId: number;
    itemId: string;
    name: string;
    icon: string | null;
    language: string | null;
    epgSourceId: number | null;
    epgChannelId: string | null;
    matchMethod: string | null;
    confidence: number;
    hasEpg: boolean;
}

export interface EpgWindowProgramme extends Programme {
    liveId: string;
    sourceId: number;
    /** Live-channel item id for Guide bucketing. */
    channelId: string;
    epgSourceId: number;
    epgChannelId: string;
}

export interface EpgWindowRequest {
    liveIds: string[];
    startMs: number;
    endMs: number;
}

export interface EpgWindowResponse {
    channels: EpgWindowChannel[];
    programmes: EpgWindowProgramme[];
    generatedAt: number;
    cacheHit: boolean;
}

/**
 * Wire shape of `GET /api/proxy/epg/:sourceId` (single source). Multi-source merge happens
 * in `useEpgStore.load()` per Phase 2.6 §A.5. **The server hardcodes a ±24h window and
 * ignores any `from`/`to` query params** (`proxy.js:1181-1184`); the typed signature does
 * not expose them.
 */
export interface EpgGetResponse {
    channels: EpgChannel[];
    programmes: Programme[];
}

/**
 * Optional server-side filters for `GET /api/proxy/epg/:sourceId` (post-4.10 §A.5).
 *
 * The server accepts every key as an additive narrowing filter; omitting a key
 * leaves it unconstrained. `channelIds` is capped server-side at 2000 ids,
 * each ≤256 chars — over the cap, the server logs a warn line and ignores
 * the param (treats the request as unfiltered for that axis).
 */
export interface EpgGetParams {
    hasEpg?: boolean;
    favoritesOnly?: boolean;
    language?: string;
    q?: string;
    channelIds?: string[];
    /**
     * Channels-only mode (post-4.10 hotfix #9). When `true`, the server
     * returns the full EPG channel catalogue for the source but skips
     * programmes entirely — `programmes: []`. The `<EpgMappingPicker>` uses
     * this on open to populate the override search across ALL EPG channels
     * (not just whatever subset Guide previously loaded via favouritesOnly)
     * without re-fetching tens of MB of programme JSON.
     */
    channelsOnly?: boolean;
}

/**
 * Response shape of `GET /api/probe?url=&ua=` (or `?t=<token>`). Server:
 * `services/probeService.js::analyzeProbeResult` returns `duration` and
 * `bitrate` in addition to the codec metadata; resolution is still read
 * from `video.videoHeight` on `loadedmetadata` and runtime bitrate is
 * tracked by hls.js via `attachBitrateListeners`.
 */
/**
 * One source audio stream from the probe (vod-audio-track-selection).
 * Server: `probeService.js::analyzeProbeResult` `audioStreams`. `index` is
 * the RELATIVE audio index — the N in ffmpeg's `-map 0:a:N` and the value
 * `TranscodeSessionPayload.audioStreamIndex` takes. Labels must derive from
 * these per-stream ffprobe tags, never from provider metadata (the
 * provider's language tags are unreliable; the file is the honest source).
 */
export interface ProbeAudioStream {
    index: number;
    /** Codec name (`'aac'`, `'eac3'`, …) — `'unknown'` when ffprobe omits it. */
    codec: string;
    /** Channel count — 0 when unknown. */
    channels: number;
    /** Raw ISO-639-2 tag, trimmed + lowercased — `'und'` when untagged. */
    language: string;
    /** ffprobe `tags.title` — null when absent. */
    title: string | null;
    /** ffprobe default disposition. */
    default: boolean;
}

/**
 * What a channel's subtitle tracks ARE, when they unanimously agree — the
 * server derives this from the actual ffprobe codec names so the UI can name
 * a reason instead of guessing one. `null` means mixed or unrecognised, and
 * the UI must then stay format-agnostic.
 *
 * - `'bitmap'`   — DVB/PGS images. No browser can render them; OCR is the
 *                  only bridge, so this is permanent.
 * - `'teletext'` — text-bearing, but decoding it needs libzvbi, which our
 *                  ffmpeg builds lack. Unavailable for a DIFFERENT reason.
 */
export type SubtitleFormat = 'bitmap' | 'teletext' | null;

/**
 * One subtitle stream as the probe sees it (vod-subtitle-restore).
 *
 * TWO indices, deliberately: `index` is the ABSOLUTE ffprobe stream index,
 * `sourceIndex` the subtitle ORDINAL that `-map 0:s:N` takes. On a real
 * 35-subtitle file the ordinals are 0..34 while the absolute indices are
 * 7..41 — passing the wrong one serves a different language than the label.
 */
export interface ProbeSubtitleStream {
    /** Absolute ffprobe stream index. */
    index: number;
    /** Subtitle ordinal — the N in `-map 0:s:N`. Use THIS for extraction. */
    sourceIndex: number;
    language: string;
    title: string | null;
    codec: string;
    /** True only for text formats, which can become WebVTT. Bitmap is false. */
    renderable: boolean;
    /** True when MPEG-TS can carry it for an external player. */
    muxableToTs: boolean;
}

export interface ProbeInfo {
    /** Video codec name (`'h264'`, `'hevc'`, `'unknown'`, …). */
    video: string;
    /** Audio codec name (`'aac'`, `'ac3'`, `'eac3'`, `'unknown'`, …). */
    audio: string;
    /** Audio channel count (1 = mono, 2 = stereo, 6 = 5.1) — 0 when unknown. */
    audioChannels?: number;
    /** Detected video width in px (0 when unknown). From `videoStream.width`. */
    width?: number;
    /** Detected video height in px (0 when unknown). From `videoStream.height`. */
    height?: number;
    /** Detected total bitrate in bits/sec (0/undefined when unknown). */
    bitrate?: number;
    /** Container format (`'mpegts'`, `'mp4'`, `'matroska'`, `'unknown'`, …). */
    container: string;
    /** Total media duration in seconds. `null` when unknown (live streams, ffprobe-failed assets). Drives the synthetic VOD HLS playlist server-side when set. */
    duration?: number | null;
    /** Average frame rate from `avg_frame_rate` (rounded to 0.1 fps). `null` when unknown. */
    frameRate?: number | null;
    /** HDR variant: `'hdr10'` (PQ), `'hlg'`, `'dovi'` (Dolby Vision), or `null` for SDR. */
    hdr?: 'hdr10' | 'hlg' | 'dovi' | null;
    /** True iff the browser can natively play this combination. */
    compatible: boolean;
    /** True iff TS → fMP4 remux is sufficient (no re-encode). */
    needsRemux: boolean;
    /** True iff video and/or audio must be transcoded. */
    needsTranscode: boolean;
    /** Server-side error message when ffprobe fails; status is still 200 in that case (legacy contract). */
    error?: string;
    /** Audio/subtitle track counts in the probed mux (0 = none detected). */
    videoTracks?: number;
    audioTracks?: number;
    subtitleTracks?: number;
    /**
     * live-subtitle-tracks — how many subtitle tracks a BROWSER can display.
     * Bitmap formats (`dvb_subtitle`, `hdmv_pgs_subtitle`) cannot become
     * WebVTT at any point in the pipeline, so this is routinely 0 while
     * `subtitleTracks` is 3. Absent on older servers.
     */
    subtitleRenderableTracks?: number;
    /** Unanimous subtitle format, for honest UI copy. See `upstreamTracks.subtitleFormat`. */
    subtitleFormat?: SubtitleFormat;
    /** Every source audio stream (vod-audio-track-selection). Absent on older servers and error-shaped results. */
    audioStreams?: ProbeAudioStream[];
    /** Every source subtitle stream (vod-subtitle-restore). Absent on older servers. */
    subtitles?: ProbeSubtitleStream[];
    /** Upstream HTTP status re-fetched after a probe failure (ffprobe swallows it). `null`/absent when unknown. */
    upstreamStatus?: number | null;
    /** Failure classification: `'stream_limit'` = provider or local concurrent-stream cap; `slotRefused` identifies local arbitration. */
    errorKind?: 'stream_limit' | null;
    /** Local source-slot refusal, with same-user sessions eligible for explicit replacement. */
    slotRefused?: boolean;
    occupyingKind?: string | null;
    occupyingChannel?: { sourceId: number; channelId: string; name?: string } | null;
    ownSessions?: OwnOccupyingSession[];
}

/**
 * POST body for `/history`. Server: `routes/history.js:86-133`. The composite
 * key on insert is `${userId}:${id}`; `data` is JSON-stringified server-side.
 * `dwellIncrement` is a non-destructive heartbeat for live channels — VOD
 * callers leave it undefined and pass `progress` + `duration` instead.
 */
export interface HistoryUpsertPayload {
    id: string;
    type: 'live' | 'movie' | 'series' | 'episode';
    sourceId: number;
    parentId?: string;
    progress?: number;
    duration?: number;
    /** Free-form metadata (title, subtitle, poster, container, season/episode). */
    data?: {
        title?: string;
        subtitle?: string;
        poster?: string;
        containerExtension?: string;
        seriesId?: string | number;
        currentSeason?: number;
        currentEpisode?: number;
        nextEpisode?: import('./player/seriesEpisodes').NextEpisodeMeta | null;
        [k: string]: unknown;
    };
    /** Live channel dwell heartbeat in seconds; VOD leaves undefined. */
    dwellIncrement?: number;
}

/** Recently-added catalog item. Server: routes/channels.js:296 returns playlist_items rows with `data` JSON-parsed. */
/** Atlas Phase 3 — channel metadata from `/channels/resolve`, including dropped
 *  rows. `inCurrentPlaylist === 0` means the provider no longer carries it. */
export interface ResolvedChannel {
    sourceId: number;
    itemId: string;
    name: string;
    streamIcon: string | null;
    inCurrentPlaylist: number;
}

export interface RecentItem {
    id: string;
    source_id: number;
    item_id: string;
    type: 'movie' | 'series';
    name: string;
    category_id: string | null;
    stream_icon: string | null;
    container_extension: string | null;
    added_at: number;
    is_hidden: number;
    data: {
        title?: string;
        subtitle?: string;
        poster?: string;
        year?: string | number;
        description?: string;
        [k: string]: unknown;
    };
}

// -------------------------- API surface

/** Atlas Phase 5 — one ranked Event Finder candidate (server → GET /api/events). */
/**
 * One channel's own EPG listing for an event. The times are that channel's, NOT
 * the event's summary window — real feeds disagree by hours (a provider's padded
 * four-hour block vs a two-hour match), which is exactly why this is per-channel.
 * `description` is whitespace-collapsed server-side and capped at 2,000 chars —
 * the pathological tail only (0.039 % of rows), since the detail popover renders
 * the whole string.
 */
export interface EventCandidateAiring {
    title: string | null;
    description: string | null;
    /** Compact episode label ('S1E4'), null when the feed carries none. */
    episodeNum: string | null;
    /** Programme artwork URL — route through `proxyImageUrl` before `<img src>`. */
    icon: string | null;
    startMs: number;
    endMs: number;
}

export interface EventCandidate {
    sourceId: number;
    itemId: string;
    name: string;
    language: string | null;
    isFavorite: boolean;
    reliability: { verdict: 'good' | 'mixed' | 'bad' | 'none'; rate: number | null; label: string };
    lastSeen: {
        height: number | null;
        bitrate: number | null;
        fps: number | null;
        videoCodec: string | null;
        audioCodec: string | null;
        audioChannels: number | null;
        /** Audio/subtitle track counts (probe-measured) — null = never measured, 0 = measured none. */
        audioTracks?: number | null;
        subtitleTracks?: number | null;
        ageMs: number;
        varies: boolean;
    } | null;
    providerClaim: string | null;
    /** Channel's group/category name (display only — Atlas R6 Event Finder legibility). */
    group?: string | null;
    airingTitle?: string | null;
    /**
     * The EPG programme THIS channel is carrying, with its own window — present
     * on the Event Finder's search / programme / failing doors, absent on
     * canonical discovery candidates (the projection's airings table stores no
     * title, and mapping a candidate back to its own airing needs a per-channel
     * route resolution the detail path doesn't do yet). Never assume it exists.
     */
    airing?: EventCandidateAiring | null;
    /** Channel logo — proxy via `proxyImageUrl`. Absent on legacy responses. */
    logo?: string | null;
    /**
     * Provider-declared archive depth in hours (0 = none). Known to overstate on
     * some providers, so eligibility is decided by `canPlayFromArchive`, which
     * clamps it against the user's `catchupWindowHours` — never trusted raw.
     */
    archiveHours?: number;
    score: number;
    breakdown: { reliability: number; language: number; quality: number; favorite: number };
}

export interface FootballFixtureCard {
    key: string;
    kickoffAt: number | null;
    home: { id: string | null; name: string; shortName?: string | null; crestUrl?: string | null };
    away: { id: string | null; name: string; shortName?: string | null; crestUrl?: string | null };
    competition: { id: string | null; groupId?: string | null; name: string; countryCode?: string | null; country?: string | null; logoUrl?: string | null };
    status: 'scheduled' | 'live' | 'finished' | 'cancelled' | 'unknown';
    candidateCount: number | null;
}

export interface FootballFixtureListResponse {
    state: 'ready' | 'stale' | 'preparing' | 'markets_required' | 'disabled';
    enabledMarkets?: string[];
    retryAfterMs?: number;
    snapshot?: {
        revision: string | number;
        fetchedAt: number | null;
        expiresAt: number | null;
        coverageByCountry: Record<string, {
            fetchedAt: number | null;
            expiresAt: number | null;
            state: 'ready' | 'error' | 'missing';
        }>;
    };
    date?: string;
    timezone?: string;
    countries?: string[];
    filters?: { competition: string | null; availableOnly: boolean; view?: string; search?: string };
    facets?: { competitions: Array<{ id: string; name: string; country?: string | null; logoUrl?: string | null; majorRank?: number | null; count: number; matchingCount?: number; favorite?: boolean }> };
    fixtures: FootballFixtureCard[];
    nextCursor: string | null;
}

export interface FootballCandidate {
    sourceId: number;
    itemId: string;
    id?: string;
    name: string;
    categoryName?: string | null;
    logo?: string | null;
    evidence: 'guide' | 'listing' | 'possible' | 'manual';
    station?: { country: string; stationId: string; name: string } | null;
    autoEligible: boolean;
    reason?: string | null;
    /** Real EPG programme evidence; present only when the guide confirms the fixture. */
    airing?: {
        sourceId: number;
        channelId: string;
        startAt: number;
        endAt: number;
        title: string | null;
        description: string | null;
    } | null;
    archiveHours?: number;
}

export interface FootballStation {
    stationId: string;
    name: string;
    type: string | null;
    broadcastStartAt?: number | null;
    broadcastEndAt?: number | null;
    excluded?: boolean;
    channels: FootballCandidate[];
}

export interface FootballFixtureDetailPayload {
    state: 'ready' | 'stale' | 'preparing' | 'error';
    fixture: FootballFixtureCard;
    broadcastersByCountry: Array<{ country: string; stations: FootballStation[] }>;
    candidates: {
        guideConfirms: FootballCandidate[];
        listings: FootballCandidate[];
        possible: FootballCandidate[];
    };
    unmatchedStations: Array<{ country: string; stationId: string; name: string; type: string | null }>;
    freshness: {
        fixtureFetchedAt: number | null;
        broadcastFetchedAtByCountry: Record<string, number | null>;
        broadcastCoverage?: Record<string, { state: 'ready' | 'stale' | 'error' | 'missing' | 'preparing'; fetchedAt: number | null; from?: number | null; to?: number | null }>;
        stale: boolean;
    };
    epgCoverage: { state: 'unavailable' | 'partial' | 'ready' };
}

export type FootballFixtureDetailResponse = FootballFixtureDetailPayload | { state: 'disabled' | 'markets_required' } | {
    state: 'preparing';
    retryAfterMs: number;
    fixtureKey: string;
} | { state: 'error'; message?: string };

export interface FootballBroadcasterLink {
    country: string;
    stationId: string;
    sourceId?: number | null;
    itemId?: string | null;
    excluded: boolean;
}

export interface FootballBroadcasterLinksResponse {
    markets: string[];
    links: FootballBroadcasterLink[];
}

export interface FootballBroadcasterLinksUpdate {
    markets?: string[];
    links?: FootballBroadcasterLink[];
    remove?: Array<{ country: string; stationId: string; sourceId?: number; itemId?: string }>;
}

/** Atlas R6 — derived signals for one channel (server → POST /api/observations/signals). */
export interface ObservationSignal {
    reliability: { verdict: 'good' | 'mixed' | 'bad' | 'none'; rate: number | null; label: string };
    lastSeen: EventCandidate['lastSeen'];
}

export interface EventFinderResult {
    mode: 'search' | 'programme' | 'failing';
    event?: { title: string; titleNorm: string; startMs?: number | null; stopMs?: number | null } | null;
    candidates: EventCandidate[];
    /** Failing mode only — the dropped channel's name, for the recovery banner. */
    failingName?: string | null;
}

export type EventDiscoverySection = 'current' | 'upcoming';
export type EventDiscoveryConfidence = 'all' | 'confirmed';
/** `'all'` hides nothing (default); `'readable'` excludes clusters the caller's
 * `langs` can't read outright. Binds a discovery cursor — changing it MUST
 * reset pagination to the first page (see `useEventDiscovery`'s `filters`
 * watcher) or a continuation cursor minted under the old value 400s. */
export type EventDiscoveryLanguageFilter = 'all' | 'readable';
/** One entry of the `languages` facet — server `label` is `Intl.DisplayNames`
 * output; never re-derive/title-case a label from `key` client-side. */
export interface EventDiscoveryLanguageFacet {
    key: string;
    label: string;
    count: number;
}

/** One entry of the `regions` facet — the channel-country/region axis
 * (`FR`, `US`, `ASIA`, …). No `label` field on purpose: server contract says
 * "the token IS the label" (`ASIA`/`AFR`/`WT` are regions, not countries, and
 * there's nothing honest to title-case them into). */
export interface EventDiscoveryRegionFacet {
    key: string;
    count: number;
}

export interface EventCandidateSummary {
    sourceId: number;
    itemId: string;
    name: string;
    logo: string | null;
    language: string | null;
    group: string | null;
    isFavorite: boolean;
    reliability: EventCandidate['reliability'];
    lastSeen: EventCandidate['lastSeen'];
    providerClaim: string | null;
}

export interface EventDiscoveryCard {
    eventKey: string;
    title: string;
    sportKey: string;
    competition: string | null;
    confidence: 'confirmed' | 'listed';
    startMs: number;
    endMs: number;
    channelCount: number;
    bestCandidate: EventCandidateSummary;
    /** Cluster-level language classification (title script / candidate-channel
     * language) — null means no confident foreign-language signal (always
     * readable). Independent of `bestCandidate.language`, which is that one
     * channel's own tag. */
    languageKey: string | null;
    /** Whether `languageKey` is readable for the request's effective `langs`. */
    languageReadable: boolean;
    /** The SAME token this cluster would need in `?languageKeys=` to be
     * selected (`'unclassified'` when `languageKey` is null) — compare
     * against the client's own filter selection without re-deriving the
     * null<->token mapping. */
    languageBucketKey: string;
}

export interface EventDiscoveryUsableResponse {
    mode: 'discover';
    snapshot: {
        generationId: number;
        activatedAt: number;
        ageMs: number;
        state: 'ready' | 'stale';
        degradedReason: null | 'child_busy' | 'sync_offload_disabled';
        asOfMs: number;
        coverageEndsAt: number;
    };
    sports: Array<{ key: string; label: string; count: number }>;
    /** Language facet, computed over the same stable browse window as `sports`
     * — independent of `sport`/`confidence`/`languageFilter`, so it never
     * jitters while the user is browsing. Drive the language control's
     * options + counts from this; never hardcode a language list. */
    languages: EventDiscoveryLanguageFacet[];
    /** Channel-country/region facet — same stability rule as `sports`/
     * `languages` (computed over the browse window, independent of any
     * active `sport`/`confidence`/`languageKeys`/`regions` selection so the
     * control never jitters). Sorted count desc then alpha; no reserved
     * "unclassified" bucket (a region-less cluster contributes to none).
     * Optional rather than required — like `useWarmStatus`'s `?? []`
     * tolerance for `warmTargets`/`rules` — so a mixed-version deploy window
     * (server ahead of / behind this client) degrades to "no region facet"
     * instead of a hard failure. */
    regions?: EventDiscoveryRegionFacet[];
    namedFixtureCount: number;
    sections: Partial<Record<EventDiscoverySection, {
        items: EventDiscoveryCard[];
        nextCursor: string | null;
    }>>;
}

export interface EventDiscoveryPreparingResponse {
    mode: 'discover';
    snapshot: {
        generationId: null;
        activatedAt: null;
        ageMs: null;
        state: 'preparing';
        degradedReason: null | 'child_busy' | 'projection_incompatible';
        asOfMs: number;
        coverageEndsAt: null;
    };
    retryAfterMs: 5000;
    sports: [];
    languages: [];
    regions?: [];
    namedFixtureCount: 0;
    sections: Partial<Record<EventDiscoverySection, never>>;
}

export type EventDiscoveryListResponse = EventDiscoveryUsableResponse | EventDiscoveryPreparingResponse;

export interface EventDiscoveryDetailResponse {
    mode: 'discover-detail';
    snapshot: { generationId: number; activatedAt: number; ageMs: number };
    event: {
        eventKey: string;
        title: string;
        memberTitleNorms: string[];
        sportKey: string;
        /** Server-owned display label — never derive one from `sportKey`. */
        sportLabel: string;
        competition: string | null;
        confidence: 'confirmed' | 'listed';
        startMs: number;
        endMs: number;
        channelCount: number;
        languageKey: string | null;
        languageReadable: boolean;
        /** Same token as the list card's `languageBucketKey`. */
        languageBucketKey: string;
    };
    candidates: Array<EventCandidate & { logo: string | null }>;
    nextCursor: string | null;
}

/** Phase 3 — one provider category whose channel names refresh can re-read. */
export interface EventRefreshCategory {
    sourceId: number;
    sourceName: string | null;
    categoryId: string;
    categoryName: string | null;
    itemCount: number;
    detectedAt: number | null;
    lastRefreshAt: number | null;
    lastRefreshStatus: 'success' | 'unreachable' | 'invalid_response' | 'not_refreshable' | 'error' | null;
    excluded: boolean;
    included: boolean;
    /** False when an override excludes it or its source is not a live Xtream source. */
    refreshable: boolean;
    playbackActive: boolean;
}

export interface EventRefreshStatus {
    enabled: boolean;
    /** Server-owned: viewers read freshness, only admins may mutate. */
    canRefresh: boolean;
    childBusy: boolean;
    nextTickAt: number | null;
    lastDispatchAt: number | null;
    lastOutcome: string | null;
    cadenceMs: number | null;
    categories: EventRefreshCategory[];
}

export interface EventRefreshResult {
    result: 'refreshed' | 'detected' | 'skipped';
    status: 'success' | 'unreachable' | 'invalid_response' | 'not_refreshable' | 'error';
    sourceId: number | null;
    categoryId: string | null;
    detected: number;
    returned: number;
    matched: number;
    updated: number;
    unchanged: number;
    durationMs: number;
}

/** 200 carries the finished result; 202 means it is still running — poll status. */
export interface EventRefreshResponse {
    accepted: boolean;
    result?: EventRefreshResult | null;
    waitedMs?: number;
}

export interface EventDiscoveryListOptions {
    langs?: string[];
    sport?: string;
    confidence?: EventDiscoveryConfidence;
    languageFilter?: EventDiscoveryLanguageFilter;
    /** Multi-select allow-list of language-facet bucket keys (real key or the
     * reserved `'unclassified'` token) — a hard membership filter, ANDed with
     * `languageFilter` rather than replacing it. Empty/absent ⇒ no filter. */
    languageKeys?: string[];
    /** Multi-select allow-list over the channel-country/region axis (`FR`,
     * `US`, `ASIA`, …) — ANY-match: a cluster survives if at least one of its
     * candidates falls in a selected region. ANDed with `sport`/`confidence`/
     * `languageKeys`, never desugared into any of them. Empty/absent ⇒ no
     * filter. Changing it MUST reset pagination to the first page (same
     * cursor-binding rule as `languageKeys` above). */
    regions?: string[];
    section?: EventDiscoverySection;
    limit?: number;
    cursor?: string;
    signal?: AbortSignal;
}

export interface EventDiscoveryDetailOptions {
    langs?: string[];
    limit?: number;
    cursor?: string;
    signal?: AbortSignal;
}

/** Atlas Phase 2 — one per-tune-in channel observation (client → POST /api/observations). */
export interface ObservationInput {
    sourceId: number;
    itemId: string;
    connected: boolean;
    droppedAfterSeconds?: number | null;
    resHeight?: number | null;
    bitrateBps?: number | null;
    fps?: number | null;
    videoCodec?: string | null;
    audioCodec?: string | null;
    audioChannels?: number | null;
    /** Track counts (probe path only — playback observations omit them). */
    audioTracks?: number | null;
    subtitleTracks?: number | null;
}

function qs(params: Record<string, unknown>): string {
    const entries = Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== false && v !== '');
    if (entries.length === 0) return '';
    const sp = new URLSearchParams();
    for (const [k, v] of entries) sp.set(k, String(v));
    return '?' + sp.toString();
}

export const api = {
    debug: {
        directLiveUrl: (sourceId: number, channelId: string) =>
            request<{ url: string }>('/debug/direct-live-url', { method: 'POST', body: { sourceId, channelId } }),
    },
    // Health & version
    health: () => request<{ ok: boolean }>('/health/live'),
    /**
     * The running server's build identity — the ONE surface that reports it to
     * a client. `commit` is null when the build could not be identified (a
     * container built with no GIT_SHA build-arg); `dirty` means the working
     * tree did not match the SHA, so the SHA does not describe what is running.
     */
    version: () =>
        request<{
            version: string;
            commit: string | null;
            shortCommit: string | null;
            dirty: boolean;
            source: 'image' | 'git' | 'unknown';
        }>('/version'),

    // Auth
    auth: {
        // Server route is `/api/auth/me` (see server/routes/auth.js:126). Was
        // calling `/auth/whoami` which falls through Express to the SPA's
        // index.html fallback (HTTP 200 + HTML body) — JSON parse silently
        // returns `null`, so `auth.user` stayed null and the admin-only
        // Settings nav link was hidden even when signed in as admin.
        whoami: () => request<User | null>('/auth/me', { allow401: true }),
        setupRequired: () => request<{ setupRequired: boolean }>('/auth/setup-required', { allow401: true }),
        setup: (username: string, password: string) =>
            request<{ token: string; user: User }>('/auth/setup', {
                method: 'POST',
                body: { username, password },
                allow401: true,
            }),
        login: (username: string, password: string) =>
            request<{ token: string; user: User }>('/auth/login', {
                method: 'POST',
                body: { username, password },
                allow401: true,
            }),
        logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
        device: (userCode: string, signal?: AbortSignal) =>
            request<{ userCode: string; status: 'pending'; expiresIn: number }>(`/auth/device?code=${encodeURIComponent(userCode)}`, { signal, allow401: true }),
        approveDevice: (userCode: string, approve: boolean) =>
            request<{ status: 'approved' | 'denied' }>('/auth/device/approve', {
                method: 'POST',
                body: { userCode, approve },
                headers: { 'X-Device-Approval': '1' },
                allow401: true,
            }),
    },

    watchTogether: {
        heartbeat: (body: { playbackId: string; playbackState: 'playing' | 'paused' | 'buffering'; measurement: WatchTogetherMeasurement; canCatchUp: boolean }) =>
            request<{ ok: boolean; snapshot: WatchTogetherSnapshot }>('/watch-together/heartbeat', { method: 'POST', body }),
        chat: (body: { playbackId: string; text: string }) => request<{ ok: boolean }>('/watch-together/chat', { method: 'POST', body }),
        reaction: (body: { playbackId: string; reaction: string }) => request<{ ok: boolean }>('/watch-together/reaction', { method: 'POST', body }),
        bringLive: (body: { playbackId: string }) => request<{ ok: boolean; recipients: number; consentExpiresAt: number; command: WatchTogetherCommandSummary }>('/watch-together/bring-live', { method: 'POST', body }),
        commandResult: (body: { playbackId: string; commandId: string; status: string }) => request<{ ok: boolean }>('/watch-together/command-result', { method: 'POST', body }),
        acceptBringLive: (body: { playbackId: string; commandId: string }) => request<{ ok: boolean; executionExpiresAt: number }>('/watch-together/bring-live/accept', { method: 'POST', body }),
    },

    // Users (admin)
    users: {
        getAll: () => request<User[]>('/auth/users'),
        create: (data: { username: string; password: string; role?: 'admin' | 'viewer' }) =>
            request<User>('/auth/users', { method: 'POST', body: data }),
        update: (id: number, data: Partial<{ username: string; password: string; role: 'admin' | 'viewer' }>) =>
            request<User>(`/auth/users/${id}`, { method: 'PUT', body: data }),
        delete: (id: number) => request<{ ok: boolean }>(`/auth/users/${id}`, { method: 'DELETE' }),
    },

    // Sources
    sources: {
        getAll: () => request<Source[]>('/sources'),
        getByType: (type: Source['type']) => request<Source[]>(`/sources/type/${type}`),
        getById: (id: number) => request<Source>(`/sources/${id}`),
        recommended: () => request<RecommendedEpgSourcesResponse>('/sources/recommended'),
        create: (data: Partial<Source>) => request<Source>('/sources', { method: 'POST', body: data }),
        update: (id: number, data: Partial<Source>) =>
            request<Source>(`/sources/${id}`, { method: 'PUT', body: data }),
        delete: (id: number) => request<{ ok: boolean }>(`/sources/${id}`, { method: 'DELETE' }),
        toggle: (id: number) => request<Source>(`/sources/${id}/toggle`, { method: 'POST' }),
        test: (id: number) => request<{ ok: boolean; success?: boolean; error?: string; message?: string; account?: unknown }>(
            `/sources/${id}/test`,
            { method: 'POST' },
        ),
        sync: (id: number) => request<{ ok: boolean }>(`/sources/${id}/sync`, { method: 'POST' }),
        getStatus: () => request<SyncStatus[]>('/sources/status'),
        // Atlas Phase 7 — per-source content scale (present-only).
        counts: () =>
            request<{
                total: number;
                bySource: Record<string, { live: number; movie: number; series: number; total: number }>;
            }>('/sources/counts'),
        estimate: (id: number) => request<M3uEstimate>(`/sources/${id}/estimate`),
        estimateByUrl: (url: string, type: Source['type']) =>
            request<M3uEstimate>('/sources/estimate', { method: 'POST', body: { url, type } }),
        getAccount: (id: number, opts?: { refresh?: boolean }) =>
            request<AccountStatus>(`/sources/${id}/account${opts?.refresh ? '?refresh=1' : ''}`),
    },

    // Channels (hidden + per-user overrides + custom groups)
    channels: {
        getHidden: (sourceId?: number) =>
            request<HiddenItem[]>(`/channels/hidden${qs({ sourceId })}`),
        hide: (sourceId: number, itemType: HiddenItemType, itemId: string) =>
            request<{ ok: boolean }>('/channels/hide', { method: 'POST', body: { sourceId, itemType, itemId } }),
        show: (sourceId: number, itemType: HiddenItemType, itemId: string) =>
            request<{ ok: boolean }>('/channels/show', { method: 'POST', body: { sourceId, itemType, itemId } }),
        isHidden: (sourceId: number, itemType: HiddenItemType, itemId: string) =>
            request<{ hidden: boolean }>(`/channels/hidden/check${qs({ sourceId, itemType, itemId })}`),
        bulkHide: (items: Array<{ sourceId: number; itemType: HiddenItemType; itemId: string }>) =>
            request<{ ok: boolean }>('/channels/hide/bulk', { method: 'POST', body: { items } }),
        bulkShow: (items: Array<{ sourceId: number; itemType: HiddenItemType; itemId: string }>) =>
            request<{ ok: boolean }>('/channels/show/bulk', { method: 'POST', body: { items } }),
        showAll: (sourceId: number, contentType: ContentType) =>
            request<{ ok: boolean }>('/channels/show/all', { method: 'POST', body: { sourceId, contentType } }),
        hideAll: (sourceId: number, contentType: ContentType) =>
            request<{ ok: boolean }>('/channels/hide/all', { method: 'POST', body: { sourceId, contentType } }),
        recent: (opts: { type: 'movie' | 'series'; limit?: number }) =>
            request<RecentItem[]>(`/channels/recent${qs({ type: opts.type, limit: opts.limit ?? 12 })}`),
        /** Atlas Phase 3 — resolve channel metadata (incl. DROPPED rows) for
         *  specific (sourceId, itemId) pairs, so favourites/history can surface a
         *  dropped channel greyed with its real name/logo instead of a placeholder. */
        resolve: (items: Array<{ sourceId: number; itemId: string }>) =>
            request<{ resolved: ResolvedChannel[] }>('/channels/resolve', {
                method: 'POST',
                body: { items },
            }),
        me: {
            list: () => request<unknown>('/channels/me'),
            update: (sourceId: number, itemId: string, patch: Record<string, unknown>) =>
                request<unknown>(`/channels/me/${sourceId}/${encodeURIComponent(itemId)}`, { method: 'PUT', body: patch }),
            clear: (sourceId: number, itemId: string) =>
                request<{ ok: boolean }>(`/channels/me/${sourceId}/${encodeURIComponent(itemId)}`, { method: 'DELETE' }),
            reorder: (groupId: string | number, ordered: string[]) =>
                request<{ ok: boolean }>('/channels/me/reorder', { method: 'POST', body: { groupId, ordered } }),
            moveAndReorder: (
                moves: Array<{ sourceId: number; itemId: string; toGroupId: string | number | null }>,
                reorder: { groupId: string | number; ordered: string[] }[],
            ) =>
                request<{ ok: boolean }>('/channels/me/move-and-reorder', {
                    method: 'POST',
                    body: { moves, reorder },
                }),
            uploadLogo: (dataUrl: string) =>
                request<{ url: string }>('/channels/me/logo', { method: 'POST', body: { dataUrl } }),
            resetAll: () => request<{ ok: boolean }>('/channels/me', { method: 'DELETE' }),
            groups: {
                list: async () => {
                    const response = await request<{ groups: CustomGroup[] }>('/channels/me/groups');
                    return response.groups;
                },
                listMerged: () => request<unknown>('/channels/me/groups/list'),
                create: (name: string) =>
                    request<CustomGroup>('/channels/me/groups', { method: 'POST', body: { name } }),
                rename: (id: number, name: string) =>
                    request<CustomGroup>(`/channels/me/groups/${id}`, { method: 'PUT', body: { name } }),
                delete: (id: number, opts: { restore?: boolean; reassignTo?: number } = {}) => {
                    const params: Record<string, unknown> = {};
                    if (opts.restore === false) params.restore = 0;
                    if (opts.reassignTo) params.reassignTo = opts.reassignTo;
                    return request<{ ok: boolean }>(`/channels/me/groups/${id}${qs(params)}`, { method: 'DELETE' });
                },
                reorder: (ordered: number[]) =>
                    request<{ ok: boolean }>('/channels/me/groups/reorder', { method: 'POST', body: { ordered } }),
            },
            // Per-user group display order. New saves use stable category keys;
            // `legacy=true` marks the title-based fallback from pre-v10 data.
            groupOrder: {
                get: () => request<{ ordered: string[]; legacy: boolean }>('/channels/me/group-order'),
                set: (ordered: string[]) =>
                    request<{ ok: boolean; count: number }>('/channels/me/group-order', {
                        method: 'POST',
                        body: { ordered },
                    }),
            },
            groupLayout: {
                get: () => request<{ groups: GroupLayoutOverride[] }>('/channels/me/group-layout'),
                update: (
                    keys: string[],
                    patch: { custom_name?: string | null; user_hidden?: boolean },
                ) =>
                    request<{ ok: boolean; count: number }>('/channels/me/group-layout', {
                        method: 'PUT',
                        body: { keys, ...patch },
                    }),
                reset: () =>
                    request<{
                        ok: boolean;
                        removed: { overrides: number; order: number };
                    }>('/channels/me/group-layout', { method: 'DELETE' }),
            },
        },
    },

    // Favorites
    favorites: {
        getAll: (sourceId?: number, itemType?: ItemType) =>
            request<Favorite[]>(`/favorites${qs({ sourceId, itemType })}`),
        add: (sourceId: number, itemId: string, itemType: ItemType = 'channel') =>
            request<Favorite>('/favorites', { method: 'POST', body: { sourceId, itemId, itemType } }),
        remove: (sourceId: number, itemId: string, itemType: ItemType = 'channel') =>
            request<{ ok: boolean }>('/favorites', { method: 'DELETE', body: { sourceId, itemId, itemType } }),
        check: (sourceId: number, itemId: string, itemType: ItemType = 'channel') =>
            request<{ favorite: boolean }>(`/favorites/check${qs({ sourceId, itemId, itemType })}`),
    },

    // Proxy / Xtream / EPG / Catchup / Cache
    playback: {
        startVodSession: (body: {
            sourceId: number | null;
            contentType: 'movie' | 'episode' | 'recording';
            contentId: string;
            title: string;
            mode: 'direct' | 'direct-hls' | 'proxy' | 'remux' | 'transcode' | 'transcode-fallback';
            transcodeSessionId?: string | null;
        }) => request<{ playbackId: string }>('/playback/vod-session', { method: 'POST', body }),
        heartbeatVodSession: (playbackId: string, body: { playbackState: 'playing' | 'paused' | 'buffering'; currentTime: number | null }) =>
            request<{ ok: boolean }>(`/playback/vod-session/${encodeURIComponent(playbackId)}/heartbeat`, { method: 'POST', body }),
        endVodSession: (playbackId: string) =>
            request<{ ok: boolean }>(`/playback/vod-session/${encodeURIComponent(playbackId)}`, { method: 'DELETE' }),
        activeSessions: () => request<{ sessions: ActivePlaybackSession[] }>('/playback/sessions'),
        forceStop: (playbackId: string) =>
            request<{ ok: boolean; stopped: boolean }>(`/playback/sessions/${encodeURIComponent(playbackId)}`, { method: 'DELETE' }),
        startLiveSession: (payload: LivePlaybackSessionRequest, signal?: AbortSignal) =>
            request<LivePlaybackSessionResult>('/playback/live-session', {
                method: 'POST',
                body: payload,
                signal,
            }),
        endLiveSession: (playbackId: string) =>
            request<{ ok: boolean; stopped?: boolean; mode?: LivePlaybackMode }>(
                `/playback/live-session/${encodeURIComponent(playbackId)}`,
                { method: 'DELETE' },
            ),
        heartbeatLiveSession: (playbackId: string) =>
            request<{ ok: boolean }>(
                `/playback/live-session/${encodeURIComponent(playbackId)}/heartbeat`,
                { method: 'POST' },
            ),
        /**
         * Mint an EXTERNAL-player session (live-ingest-tee + remediation):
         * slot arbitration + preflight run server-side BEFORE the player
         * opens; `live-hls` mode (the default) returns the full-fidelity
         * tee served as real HLS, `live-ts` (opt in via `transport: 'ts'`,
         * not exposed in the UI) returns the continuous raw-TS URL,
         * `proxy-hls` is the rewind-disabled fallback (today's behavior).
         */
        startExternalSession: (payload: ExternalPlaybackSessionRequest) =>
            request<ExternalPlaybackSessionResult>('/playback/external-session', {
                method: 'POST',
                body: payload,
            }),
    },

    // Shared live buffers — Server: routes/liveBuffer.js.
    liveBuffer: {
        status: () => request<LiveBufferStatus>('/live-buffer/status'),
        rules: () => request<{ rules: LiveBufferRule[] }>('/live-buffer/rules'),
        replaceRules: (rules: LiveBufferRuleInput[]) =>
            request<{ rules: LiveBufferRule[] }>('/live-buffer/rules', {
                method: 'PUT',
                body: { rules },
            }),
        enableRule: (id: number) =>
            request<{ rule: LiveBufferRule }>(`/live-buffer/rules/${encodeURIComponent(String(id))}/enable`, {
                method: 'POST',
            }),
        disableRule: (id: number) =>
            request<{ rule: LiveBufferRule }>(`/live-buffer/rules/${encodeURIComponent(String(id))}/disable`, {
                method: 'POST',
            }),
    },

    vodMeta: {
        facets: (params: VodMetaFacetParams) =>
            request<VodMetaFacets>(
                `/vod-meta/facets${qs({
                    kind: params.kind,
                    sourceId: params.sourceId,
                    category: params.category,
                    genre: params.genre,
                    person: params.person,
                    q: params.q,
                    searchPlot: params.searchPlot ? 1 : undefined,
                })}`,
            ),
        status: () => request<VodEnrichmentStatus>('/vod-meta/status'),
        control: (action: 'start' | 'pause', lane?: VodEnrichmentLane) =>
            request<VodEnrichmentStatus>('/vod-meta/backfill', {
                method: 'POST',
                body: { action, lane },
            }),
    },

    /**
     * Per-user episode watched state — Server: routes/vodWatchState.js.
     *
     * The client sends `(sourceId, seriesId)`, never an entity id: the server
     * resolves the entity through the same scoped query the detail page uses, so
     * these calls inherit the adult gate and cannot act on a title the caller may not
     * see. Every write answers with the season's FRESH state, so an optimistic update
     * reconciles from the reply rather than firing a second read.
     */
    vodWatchState: {
        season: (sourceId: number, seriesId: string | number, season: number | string) =>
            request<VodSeasonWatchState>(
                `/vod-watch-state/season${qs({ sourceId, seriesId, season })}`,
            ),
        /** One episode. `watched: false` is the un-mark. */
        setEpisode: (body: {
            sourceId: number;
            seriesId: string | number;
            season: number | string;
            episode: number | string;
            watched: boolean;
        }) => request<VodSeasonWatchState>('/vod-watch-state/episode', { method: 'PUT', body }),
        /** The whole season. `providerEpisodeNumbers` is unioned with TMDB's list server-side. */
        setSeason: (body: {
            sourceId: number;
            seriesId: string | number;
            season: number | string;
            watched: boolean;
            providerEpisodeNumbers?: number[];
        }) => request<VodSeasonWatchState>('/vod-watch-state/season', { method: 'PUT', body }),
        /** This episode and every one before it — the catch-up action. */
        setThrough: (body: {
            sourceId: number;
            seriesId: string | number;
            season: number | string;
            episode: number | string;
            providerEpisodeNumbers?: number[];
        }) => request<VodSeasonWatchState>('/vod-watch-state/through', { method: 'PUT', body }),
    },

    vodLibrary: {
        /** `lane` opts the call into newest-request-wins cancellation. */
        entities: (query: VodLibraryQuery, opts: { lane?: string } = {}) =>
            request<VodLibraryPage>(`/vod-library/entities${vodLibraryQs(query)}`, {
                signal: latestOnly(opts.lane),
            }),
        facets: (query: VodLibraryQuery, opts: { lane?: string } = {}) =>
            request<VodLibraryFacets>(`/vod-library/facets${vodLibraryQs(query)}`, {
                signal: latestOnly(opts.lane),
            }),
        entity: (identityKey: string) =>
            request<VodLibraryEntityDetail>(
                `/vod-library/entities/${encodeURIComponent(identityKey)}`,
            ),
        setState: (identityKey: string, patch: VodLibraryStatePatch) =>
            request<VodLibraryEntityState>(
                `/vod-library/entities/${encodeURIComponent(identityKey)}/state`,
                { method: 'PUT', body: patch },
            ),
    },

    // Proxy / Xtream / EPG / Catchup / Cache
    proxy: {
        xtream: {
            auth: (sourceId: number) => request<unknown>(`/proxy/xtream/${sourceId}/auth`),
            liveCategories: (sourceId: number, opts: { includeHidden?: boolean } = {}) =>
                request<unknown>(`/proxy/xtream/${sourceId}/live_categories${qs({ includeHidden: opts.includeHidden })}`),
            liveStreams: (sourceId: number, categoryId?: string, opts: { includeHidden?: boolean } = {}) =>
                request<unknown>(
                    `/proxy/xtream/${sourceId}/live_streams${qs({
                        category_id: categoryId,
                        includeHidden: opts.includeHidden,
                    })}`,
                ),
            vodCategories: (sourceId: number, opts: { includeHidden?: boolean } = {}) =>
                request<unknown>(`/proxy/xtream/${sourceId}/vod_categories${qs({ includeHidden: opts.includeHidden })}`),
            vodStreams: (sourceId: number, categoryId?: string, opts: VodCatalogFilterOptions = {}) =>
                request<unknown>(
                    `/proxy/xtream/${sourceId}/vod_streams${qs({
                        category_id: categoryId,
                        includeHidden: opts.includeHidden,
                        genre: opts.genre,
                        person: opts.person,
                        q: opts.q,
                        searchPlot: opts.searchPlot ? 1 : undefined,
                    })}`,
                ),
            seriesCategories: (sourceId: number, opts: { includeHidden?: boolean } = {}) =>
                request<unknown>(
                    `/proxy/xtream/${sourceId}/series_categories${qs({ includeHidden: opts.includeHidden })}`,
                ),
            series: (sourceId: number, categoryId?: string, opts: VodCatalogFilterOptions = {}) =>
                request<unknown>(
                    `/proxy/xtream/${sourceId}/series${qs({
                        category_id: categoryId,
                        includeHidden: opts.includeHidden,
                        genre: opts.genre,
                        person: opts.person,
                        q: opts.q,
                        searchPlot: opts.searchPlot ? 1 : undefined,
                    })}`,
                ),
            /**
             * `season` scopes the on-demand TMDB episode detail in the response.
             * Episode overviews are fetched per season, so the caller has to say
             * which one it is showing or it gets the default season's text.
             */
            seriesInfo: (sourceId: number, seriesId: string, season?: string | number | null) =>
                request<SeriesInfo>(
                    `/proxy/xtream/${sourceId}/series_info?series_id=${encodeURIComponent(seriesId)}`
                    + (season == null || season === '' ? '' : `&season=${encodeURIComponent(String(season))}`),
                ),
            vodInfo: (sourceId: number, vodId: string) =>
                request<{ info: VodInfo | null; variants: Variant[]; metadata?: VodDetailMetadata | null }>(
                    `/proxy/xtream/${sourceId}/vod/${encodeURIComponent(vodId)}/info`,
                ),
            shortEpg: (sourceId: number, streamId: string) =>
                request<unknown>(`/proxy/xtream/${sourceId}/short_epg?stream_id=${encodeURIComponent(streamId)}`),
            getStreamUrl: (sourceId: number, streamId: string, type: 'live' | 'movie' | 'series' = 'live', container = 'm3u8') =>
                request<{ url: string }>(
                    `/proxy/xtream/${sourceId}/stream/${encodeURIComponent(streamId)}/${type}?container=${container}`,
                ),
        },
        epg: {
            // Server: routes/proxy.js EPG handler — hardcoded ±24h window;
            // `from`/`to` query params are ignored. Optional `params` (post-4.10
            // §A.5) narrows the response server-side so we don't fetch a full
            // 50k-channel ±24h payload just to render a few hundred favourite
            // rows. Old callers (`useEpgStore.load`, `EpgMappingPicker`) pass
            // nothing and get the unfiltered legacy payload.
            get: (sourceId: number, params?: EpgGetParams) => {
                let query = '';
                if (params) {
                    const parts: string[] = [];
                    if (params.hasEpg) parts.push('hasEpg=1');
                    if (params.favoritesOnly) parts.push('favoritesOnly=1');
                    if (params.language) parts.push(`language=${encodeURIComponent(params.language)}`);
                    if (params.q && params.q.length > 0) parts.push(`q=${encodeURIComponent(params.q)}`);
                    if (params.channelIds && params.channelIds.length > 0) {
                        // Comma-join unencoded matches the server's split-on-',' parser.
                        // Individual ids may contain reserved chars — encode each one.
                        parts.push(
                            `channelIds=${params.channelIds.map(encodeURIComponent).join(',')}`,
                        );
                    }
                    if (params.channelsOnly) parts.push('channelsOnly=1');
                    if (parts.length > 0) query = `?${parts.join('&')}`;
                }
                return request<EpgGetResponse>(`/proxy/epg/${sourceId}${query}`);
            },
            window: (payload: EpgWindowRequest) =>
                request<EpgWindowResponse>('/proxy/epg/window', { method: 'POST', body: payload }),
        },
        cache: {
            clear: (sourceId: number) => request<{ ok: boolean }>(`/proxy/cache/${sourceId}`, { method: 'DELETE' }),
        },
        /**
         * URL builders for the proxy.stream endpoints. Server:
         * `routes/proxy.js:1410+` (`/stream`, `/stream.m3u8`, `/stream.ts`,
         * `/stream.mp4`). HEAD is NOT supported — Range probes use GET with
         * `Range: bytes=0-1` + AbortController per Phase 2.7 §A.useVodStreamUrl
         * Priority 0.5 (Spec 16.5). `t` (token) is the supported form;
         * raw provider URLs must first be tokenized via POST /stream-token.
         */
        stream: {
            url: (url: string, token?: string): string => {
                if (token) return `/api/proxy/stream?t=${encodeURIComponent(token)}`;
                const parsed = parseClientUrl(url);
                if (parsed.pathname.startsWith('/api/proxy/stream') && parsed.searchParams.get('t')) {
                    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
                }
                throw new Error('stream token required; call api.proxy.stream.tokenize() for raw URLs');
            },
            tokenize: (
                payload: { url: string; streamType?: 'live' | 'movie' | 'series' | 'catchup'; container?: string },
                signal?: AbortSignal,
            ) => request<{ url: string }>('/proxy/stream-token', { method: 'POST', body: payload, signal }),
        },
    },

    // Transcode — HLS session lifecycle. Server: routes/transcode.js.
    // Force-transcode and force-video-transcode paths POST a session, get
    // back a playlist URL, then `<video src=>` it. The session is keyed by
    // upstream URL + selected audio track (vod-audio-track-selection), so
    // re-POSTing the same pair yields the existing session while a track
    // switch always gets a fresh one.
    transcode: {
        // X1 — accepts EITHER the existing url-based shape OR the
        // recording-scoped `{recordingId, seekOffset?}` shape (see
        // `TranscodeSessionRecordingPayload`). Response shape is identical.
        startSession: (
            payload: TranscodeSessionPayload | TranscodeSessionRecordingPayload,
            signal?: AbortSignal,
        ) => request<TranscodeSessionResult>('/transcode/session', { method: 'POST', body: payload, signal }),
        endSession: (sessionId: string) =>
            request<{ ok: boolean }>(`/transcode/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }),
        status: (sessionId: string) =>
            request<{ id: string; ready: boolean; segments: number }>(
                `/transcode/${encodeURIComponent(sessionId)}/status`,
            ),
    },

    subtitle: {
        /**
         * Ask the server to extract this item's subtitles NOW, in the
         * background, so the viewer's first language pick is instant.
         *
         * Fire-and-forget: resolves as soon as the server has accepted the
         * job, never waits for the extraction. Errors are the caller's to
         * swallow — a failed warm must be invisible, because the normal
         * on-demand path still works.
         */
        warm: (sourceId: number, type: 'movie' | 'series', streamId: string, container?: string) => {
            const qs = container ? `?container=${encodeURIComponent(container)}` : '';
            return request<{ status: string; reason?: string }>(
                // `request()` prepends '/api' — a leading '/api' here produced
                // `/api/api/...` and a silent 404 that the caller's catch
                // swallowed, so the warm never ran and nothing said so.
                `/subtitle/vod/${sourceId}/${type}/${encodeURIComponent(streamId)}/warm${qs}`,
                { method: 'POST' },
            );
        },
    },

    /**
     * ffprobe-backed codec detection. Server: `routes/probe.js:28-113`. Used
     * by Phase 2.7 `useVodStreamUrl` Priority 0 (autoTranscode) to decide
     * between transcode / remux / direct paths. 5-min TTL cache keyed by URL +
     * UA. Pass either `token` or an already-tokenized NodeCast proxy `url`.
     * Raw provider URLs are rejected client-side so browser-visible request
     * URLs never carry upstream stream paths.
     */
    probe: {
        run: (params: {
            url?: string;
            token?: string;
            ua?: string;
            source?: string;
            signal?: AbortSignal;
            /** X2 (ux-remediation D3) — explicit user Retry bypasses the 5-15s probe cache; omitted (not `false`) on first load so the query string is completely unchanged. */
            fresh?: boolean;
        }) => {
            if (params.url) {
                const parsed = parseClientUrl(params.url);
                if (!parsed.pathname.startsWith('/api/proxy/stream') || !parsed.searchParams.get('t')) {
                    throw new Error('stream token required; call api.proxy.stream.tokenize() for raw URLs');
                }
            }
            return request<ProbeInfo>(
                `/probe${qs({
                    url: params.url,
                    t: params.token,
                    ua: params.ua,
                    source: params.source,
                    fresh: params.fresh ? 1 : undefined,
                })}`,
                { signal: params.signal },
            );
        },
    },

    /**
     * Container-only remux (TS → fMP4). Server: `routes/remux.js:21-185`.
     * Used by Phase 2.7 `useVodStreamUrl` Priority 0 (when probe says
     * `needsRemux`) and Priority 3 (forceRemux + isRawTs). Returns a URL
     * string; the actual stream comes from the server's piped ffmpeg stdout.
     */
    remux: {
        url: (url: string, token?: string): string => {
            if (token) return `/api/remux?t=${encodeURIComponent(token)}`;
            const parsed = parseClientUrl(url);
            if (!parsed.pathname.startsWith('/api/proxy/stream') || !parsed.searchParams.get('t')) {
                throw new Error('stream token required; call api.proxy.stream.tokenize() for raw URLs');
            }
            return `/api/remux?url=${encodeURIComponent(url)}`;
        },
    },

    // Recordings — DVR. Server: routes/recording.js (singular file).
    recording: {
        list: () => request<Recording[]>('/recording'),
        bufferExportOptions: (playbackId: string) =>
            request<BufferExportOptions>('/recording/buffer-export/options', {
                method: 'POST',
                body: { playbackId },
            }),
        exportBuffer: (payload: BufferExportRequest) =>
            request<BufferExportResult>('/recording/buffer-export', {
                method: 'POST',
                body: payload,
            }),
        schedule: (payload: ScheduleRecordingPayload) =>
            request<{
                id: string;
                status: RecordingStatus;
                scheduledStartMs: number;
                durationSeconds: number;
                path: string;
                /**
                 * X4 (ux-remediation #9) — soft schedule-time conflict
                 * warning. Omitted entirely when there's nothing to warn
                 * about; scheduling always succeeds either way.
                 */
                overlapWarnings?: RecordingOverlapWarning[];
            }>('/recording/schedule', { method: 'POST', body: payload }),
        // X0 — DVR data-safety. Non-destructive: stops an actively-recording
        // sink, finalizes the file, and KEEPS it. 409 `not_recording` when the
        // row isn't currently recording (scheduled/cancelled); a no-op 200
        // `{alreadyFinished: true}` when it already stopped server-side.
        stop: (id: string) =>
            request<RecordingStopResult>(`/recording/${encodeURIComponent(id)}/stop`, {
                method: 'POST',
            }),
        // X0 — `force: true` appends `?force=1`, which on an actively-`recording`
        // row stops the sink AND deletes the finalized file in one server call
        // (the only way "Stop and delete" is actually both). Without force, a
        // `recording`-status row 409s `use_stop_first` — DELETE alone can no
        // longer silently destroy a live capture.
        delete: (id: string, opts?: { force?: boolean }) =>
            request<RecordingDeleteResult>(
                `/recording/${encodeURIComponent(id)}${opts?.force ? '?force=1' : ''}`,
                { method: 'DELETE' },
            ),
        // Spec C — partial update; server validates length + type + scope.
        update: (id: string, payload: RecordingPatchPayload) =>
            request<RecordingWithOverlapWarnings>(`/recording/${encodeURIComponent(id)}`, {
                method: 'PATCH',
                body: payload,
            }),
        // Spec 25 (post-4.7) — cascade-cancel a recurring schedule's parent
        // + every future scheduled child in one server transaction. Past
        // children (any non-'scheduled' status) are left intact.
        cancelRecurrence: (id: string) =>
            request<{ ok: boolean; deleted: number }>(
                `/recording/${encodeURIComponent(id)}/recurrence`,
                { method: 'DELETE' },
            ),
        retryOffload: (id: string) =>
            request<{ ok: boolean }>(`/recording/${encodeURIComponent(id)}/retry-offload`, {
                method: 'POST',
            }),
        // The recording file is loaded by `<video src>`, which can't attach
        // an Authorization header — auth rides the cookie path. Helper
        // returns the URL string so callers wire it directly into the
        // player without a fetch round-trip.
        fileUrl: (id: string) => `/api/recording/${encodeURIComponent(id)}/file`,
        downloadUrl: (id: string) => `/api/recording/${encodeURIComponent(id)}/file?download=1`,
        // Spec F — disk-headroom snapshot for the StoragePanel.
        storage: () => request<StorageSnapshot>('/recording/storage'),
    },

    // Watch history — Server: routes/history.js.
    history: {
        recentChannels: (limit = 10) =>
            request<ChannelViewHistoryItem[]>(`/history/live/recent${qs({ limit })}`),
        removeChannel: (sourceId: number, channelId: string) =>
            request<{ success: boolean; deleted: number }>(
                `/history/live/${encodeURIComponent(String(sourceId))}/${encodeURIComponent(channelId)}`,
                { method: 'DELETE' },
            ),
        list: (opts: {
            limit?: number;
            type?: 'live' | 'movie' | 'series' | 'episode' | 'all';
            includeHidden?: boolean;
            itemId?: string;
            sourceId?: number;
            continueWatching?: boolean;
        } = {}) =>
            request<HistoryItem[]>(
                `/history${qs({
                    limit: opts.limit,
                    type: opts.type,
                    includeHidden: opts.includeHidden ? 1 : undefined,
                    itemId: opts.itemId,
                    sourceId: opts.sourceId,
                    continueWatching: opts.continueWatching ? 1 : undefined,
                })}`,
            ),
        // POST (NOT DELETE) — keeps progress data, just hides from
        // Continue Watching. Server: routes/history.js:141.
        hide: (itemId: string) =>
            request<{ success: boolean }>(`/history/${encodeURIComponent(itemId)}/hide`, {
                method: 'POST',
            }),
        // Atlas Phase 7 — undo a Continue-Watching "Remove" or "Mark watched" (clears hidden + watched).
        unhide: (itemId: string) =>
            request<{ success: boolean }>(`/history/${encodeURIComponent(itemId)}/unhide`, {
                method: 'POST',
            }),
        // Atlas Phase 7 — mark a VOD item watched (drops off the rail, badges in Movies/Series).
        markWatched: (itemId: string) =>
            request<{ success: boolean }>(`/history/${encodeURIComponent(itemId)}/watched`, {
                method: 'POST',
            }),
        /**
         * 10-second-interval upsert from the watch page. Server:
         * `routes/history.js:86-133` (composite `${userId}:${id}` key,
         * progress / duration set only if new value > 0, dwell_seconds
         * accumulated). Phase 2.7 `useWatchHistoryTracker` calls this
         * while playing. Body matches `HistoryUpsertPayload`; `data` is
         * JSON-stringified server-side.
         */
        upsert: (payload: HistoryUpsertPayload) =>
            request<{ success: boolean; timestamp: number }>('/history', {
                method: 'POST',
                body: payload,
            }),
    },

    // X5 (ux-remediation U4/D8-final) — per-user preferences. Any role may
    // read/write; there is no admin-only verb (unlike `settings` below).
    me: {
        preferences: {
            get: () => request<UserPreferences>('/me/preferences'),
            update: (patch: Partial<UserPreferences>) =>
                request<UserPreferences>('/me/preferences', { method: 'PUT', body: patch }),
        },
        footballBroadcasterLinks: {
            get: () => request<FootballBroadcasterLinksResponse>('/me/football/broadcaster-links'),
            update: (data: FootballBroadcasterLinksUpdate) =>
                request<FootballBroadcasterLinksResponse>('/me/football/broadcaster-links', { method: 'PUT', body: data }),
        },
    },

    // Settings
    settings: {
        get: () => request<SettingsSnapshot>('/settings'),
        update: (data: Record<string, unknown>) =>
            request<SettingsSnapshot>('/settings', { method: 'PUT', body: data }),
        updateMetadata: (data: MetadataSettings) =>
            request<SettingsSnapshot>('/settings', { method: 'PUT', body: data }),
        reset: () => request<SettingsSnapshot>('/settings', { method: 'DELETE' }),
        getDefaults: () => request<SettingsSnapshot>('/settings/defaults'),
        /**
         * Write-only. An empty string clears the stored value and falls the
         * server back to its TMDB_* environment variables. The response carries
         * presence booleans only — the value is never readable afterwards.
         */
        setTmdbCredentials: (body: { readToken?: string; apiKey?: string }) =>
            request<TmdbCredentialPresence>('/settings/tmdb-credentials', {
                method: 'PUT',
                body,
            }),
        /**
         * Validate against TMDB. With no argument, tests whatever is currently in
         * effect; with one, tests that value WITHOUT storing it.
         */
        testTmdbCredentials: (body?: { readToken?: string; apiKey?: string }) =>
            request<TmdbCredentialTest>('/settings/tmdb-credentials/test', {
                method: 'POST',
                body: body ?? {},
            }),
        /**
         * D7 (ux-remediation X6) — set or change the adult-content PIN.
         * Admin-only server-side (`requireAdmin` at `settings.js`'s
         * `POST /adult/pin`). 4-8 digits, validated server-side too; the PIN
         * is never echoed back or logged (see `redactUrlForLog` + the
         * generic `request()` error path, neither of which surface the body).
         */
        setAdultPin: (pin: string) =>
            request<AdultPinSetResult>('/settings/adult/pin', { method: 'POST', body: { pin } }),
    },

    // Atlas Phase 2 — per-tune-in channel observations (reliability/quality accumulation).
    observations: {
        record: (observations: ObservationInput[]) =>
            request<{ ok: boolean; recorded: number }>('/observations', {
                method: 'POST',
                body: { observations },
            }),
        // Atlas R6 (findings #17/#18) — batch derived signals for a set of channels.
        signals: (channels: Array<{ sourceId: number; itemId: string }>) =>
            request<{ signals: Record<string, ObservationSignal> }>('/observations/signals', {
                method: 'POST',
                body: { channels },
            }),
    },

    // Atlas Phase 5 — Event Finder (the spine). Three entry doors, one ranked list.
    events: {
        find: (opts: {
            q?: string;
            title?: string;
            titleNorm?: string;
            failingSourceId?: number;
            failingItemId?: string;
            langs?: string[];
        }) =>
            request<EventFinderResult>(
                `/events${qs({
                    q: opts.q,
                    title: opts.title,
                    titleNorm: opts.titleNorm,
                    failingSourceId: opts.failingSourceId,
                    failingItemId: opts.failingItemId,
                    langs: opts.langs && opts.langs.length ? opts.langs.join(',') : undefined,
                })}`,
            ),
        discover: (opts: EventDiscoveryListOptions = {}) =>
            request<EventDiscoveryListResponse>(
                `/events/discover${qs({
                    langs: opts.langs && opts.langs.length ? opts.langs.join(',') : undefined,
                    sport: opts.sport,
                    confidence: opts.confidence,
                    languageFilter: opts.languageFilter,
                    languageKeys: opts.languageKeys && opts.languageKeys.length ? opts.languageKeys.join(',') : undefined,
                    regions: opts.regions && opts.regions.length ? opts.regions.join(',') : undefined,
                    section: opts.section,
                    limit: opts.limit,
                    cursor: opts.cursor,
                })}`,
                { signal: opts.signal },
            ),
        discoveryDetail: (eventKey: string, opts: EventDiscoveryDetailOptions = {}) =>
            request<EventDiscoveryDetailResponse>(
                `/events/discover/${encodeURIComponent(eventKey)}${qs({
                    langs: opts.langs && opts.langs.length ? opts.langs.join(',') : undefined,
                    limit: opts.limit,
                    cursor: opts.cursor,
                })}`,
                { signal: opts.signal },
            ),
        fixtures: (opts: {
            date: string;
            timezone: string;
            countries: readonly string[];
            competition?: string | null;
            availableOnly?: boolean;
            view?: 'all' | 'live' | 'upcoming' | 'favourites';
            search?: string;
            limit?: number;
            cursor?: string | null;
            signal?: AbortSignal;
        }) =>
            request<FootballFixtureListResponse>(
                `/events/fixtures${qs({
                    date: opts.date,
                    timezone: opts.timezone,
                    countries: opts.countries.join(','),
                    competition: opts.competition || undefined,
                    availableOnly: opts.availableOnly ? 1 : undefined,
                    view: opts.view && opts.view !== 'all' ? opts.view : undefined,
                    q: opts.search || undefined,
                    limit: opts.limit,
                    cursor: opts.cursor,
                })}`,
                { signal: opts.signal },
            ),
        fixture: (fixtureKey: string, opts: {
            date?: string;
            timezone?: string;
            countries?: readonly string[];
            signal?: AbortSignal;
        } = {}) =>
            request<FootballFixtureDetailResponse>(
                `/events/fixtures/${encodeURIComponent(fixtureKey)}${qs({
                    date: opts.date,
                    timezone: opts.timezone,
                    countries: opts.countries?.join(','),
                })}`,
                { signal: opts.signal },
            ),
        refreshStatus: (opts: { signal?: AbortSignal } = {}) =>
            request<EventRefreshStatus>('/events/refresh/status', { signal: opts.signal }),
        refreshCategory: (sourceId: number, categoryId: string, opts: { force?: boolean } = {}) =>
            request<EventRefreshResponse>('/events/refresh', {
                method: 'POST',
                body: { sourceId, categoryId, force: opts.force === true },
            }),
    },
};

export type Api = typeof api;

/** Friendly playback-error formatter — ports formatPlaybackError from the legacy SPA. */
export function formatPlaybackError(err: unknown, fallback = 'Failed to play content'): string {
    if (err instanceof ApiError && (err.status === 403 || err.status === 429)) {
        return (
            'Provider rejected the stream. If your account limits concurrent ' +
            'streams, close another player and try again.'
        );
    }
    return fallback;
}
